



import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
// import "./Management2.css"; // Assuming this CSS file exists and is needed
import {
    FaEdit,
    FaPlus,
    FaSave,
    FaTrash,
    FaTimes,
    FaBookmark,
    FaLayerGroup,
    FaMinusCircle,
    FaCalendarAlt,
    FaClock,
    FaListAlt,
    FaUserPlus,
    FaUsers,
} from "react-icons/fa";
import "./Tablereservation.css"; // Assuming this CSS file exists and is needed

const vendorId = localStorage.getItem("vendorId");
const authToken = localStorage.getItem("authToken");

// if (!vendorId || !authToken) {
//   alert("Vendor ID or Auth Token is missing. Please log in again.");
//   throw new Error("Vendor ID or Auth Token is missing.");
// }

// --- Restaurant Configuration ---
const RESTAURANT_OPEN_TIME_STR = "10:00";
const RESTAURANT_CLOSE_TIME_STR = "23:00";

// --- Booking Configuration (Default values removed as requested) ---

const tableStatuses = [
    "Available",
    "Reserved",
    "Occupied",
    "Cleaning",
    "Maintenance",
];
const statusColors = {
    Available: "#5cb85c",
    Reserved: "#f0ad4e",
    Occupied: "#d9534f",
    Cleaning: "#5bc0de",
    Maintenance: "#777777",
};
const waitlistStatusColors = {
    waiting: "#6c757d",
    notified: "#17a2b8",
    seated: "#28a745",
    cancelled: "#dc3545",
};

const MAX_MINUTES_IN_DAY = 24 * 60;

// ====================================================================
// =================== POLLING FIX: useInterval HOOK ==================
// This custom hook sets up an interval that is "smart" about updates.
// It uses a ref to always have the latest callback without re-creating the interval.
// ====================================================================
function useInterval(callback, delay) {
    const savedCallback = useRef();

    // Remember the latest callback.
    useEffect(() => {
        savedCallback.current = callback;
    }, [callback]);

    // Set up the interval.
    useEffect(() => {
        function tick() {
            savedCallback.current();
        }
        if (delay !== null) {
            const id = setInterval(tick, delay);
            return () => clearInterval(id);
        }
    }, [delay]);
}


// This helper function maps the API/local status to a consistent display status.
const getDisplayStatus = (seatingStatus) => {
    if (!seatingStatus) {
        return "Maintenance"; // Default if status is null or undefined
    }
    const status = String(seatingStatus).toLowerCase();
    switch (status) {
        case "vacant":
        case "available":
            return "Available";
        case "occupied":
            return "Occupied";
        case "reserved":
            return "Reserved";
        case "cleaning":
            return "Cleaning";
        case "maintenance":
            return "Maintenance";
        default:
            console.warn(
                `Unknown seatingStatus: "${seatingStatus}". Defaulting to Maintenance.`
            );
            return "Maintenance";
    }
};

/**
 * [CORRECTED FUNCTION]
 * Calculates the visual status of a table for a specific point in time,
 * considering both real-time API status and scheduled bookings.
 * @param {object} table - The table object, including its real-time status and bookings.
 * @param {string} selectedDate - The date from the global filter (e.g., "2023-10-27").
 * @param {string} selectedTime - The time from the global filter (e.g., "16:30").
 * @param {string} actualCurrentDate - The real, live date from the system clock.
 * @param {string} actualCurrentTime - The real, live time from the system clock.
 * @returns {string} The computed display status ('Available', 'Reserved', 'Occupied', etc.).
 */
const getComputedTableStatus = (table, selectedDate, selectedTime, actualCurrentDate, actualCurrentTime) => {
    const rawStatus = table.seatingStatus;
    const normalizedStatus = getDisplayStatus(rawStatus);


    // Always show Occupied / Cleaning / Maintenance as is
    if (["Occupied", "Cleaning", "Maintenance"].includes(normalizedStatus)) {
        return normalizedStatus;
    }

    const selectedTimeMinutes = timeToMinutes(selectedTime);

    if (selectedTimeMinutes !== null) {
        const conflictingBooking = (table.bookings || []).find(b => {
            if (b.date !== selectedDate) return false;
            const start = timeToMinutes(b.time);
            const end = timeToMinutes(b.endTime);
            return start !== null && end !== null && isTimeInInterval(selectedTimeMinutes, start, end);
        });

        if (conflictingBooking) {
            return "Reserved"; // Only mark Reserved if there’s a booking in future/past
        } else {
            return "Available";
        }
    }

    // Whole day view: show API status for real-time, else Available
    if (selectedTimeMinutes === null) {
        return selectedDate === actualCurrentDate ? normalizedStatus : "Available";
    }

    return "Maintenance"; // fallback
};



// --- Helper Functions (No changes needed here) ---
const getTodayDate = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
        2,
        "0"
    )}-${String(d.getDate()).padStart(2, "0")}`;
};
const getDefaultStartTime = () => {
    const d = new Date();
    let m = d.getMinutes();
    if (m === 0) {
    } else if (m <= 15) m = 15;
    else if (m <= 30) m = 30;
    else if (m <= 45) m = 45;
    else {
        m = 0;
        d.setHours(d.getHours() + 1);
    }
    let h = d.getHours();
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
};
const calculateEndTimeFromDuration = (startTimeStr, durationMinutes) => {
    if (
        !startTimeStr ||
        timeToMinutes(startTimeStr) === null ||
        durationMinutes == null ||
        isNaN(durationMinutes) ||
        durationMinutes < 0
    )
        return null;
    const [hours, minutes] = startTimeStr.split(":").map(Number);
    if (isNaN(hours) || isNaN(minutes)) return null;
    const startDate = new Date(2000, 0, 1, hours, minutes);
    const endDate = new Date(startDate.getTime() + durationMinutes * 60000);
    return `${String(endDate.getHours()).padStart(2, "0")}:${String(
        endDate.getMinutes()
    ).padStart(2, "0")}`;
};
const timeToMinutes = (timeStr) => {
    if (!timeStr || typeof timeStr !== "string" || !timeStr.includes(":"))
        return null;
    const parts = timeStr.split(":");
    if (parts.length !== 2) return null;
    const hours = parseInt(parts[0], 10);
    const minutes = parseInt(parts[1], 10);
    if (
        isNaN(hours) ||
        isNaN(minutes) ||
        hours < 0 ||
        hours > 23 ||
        minutes < 0 ||
        minutes > 59
    )
        return null;
    return hours * 60 + minutes;
};
const isTimeInInterval = (
    timeMinutes,
    intervalStartMinutes,
    intervalEndMinutes
) => {
    if (
        timeMinutes === null ||
        intervalStartMinutes === null ||
        intervalEndMinutes === null
    )
        return false;
    if (intervalStartMinutes <= intervalEndMinutes) {
        return (
            timeMinutes >= intervalStartMinutes && timeMinutes < intervalEndMinutes
        );
    } else {
        // Interval wraps around midnight
        return (
            (timeMinutes >= intervalStartMinutes &&
                timeMinutes < MAX_MINUTES_IN_DAY) ||
            (timeMinutes >= 0 && timeMinutes < intervalEndMinutes)
        );
    }
};
const isTimeOverlap = (start1Str, end1Str, start2Str, end2Str) => {
    const s1 = timeToMinutes(start1Str);
    const e1 = timeToMinutes(end1Str);
    const s2 = timeToMinutes(start2Str);
    const e2 = timeToMinutes(end2Str);

    if (s1 === null || e1 === null || s2 === null || e2 === null) return false;

    const getIntervals = (startMinutes, endMinutes) => {
        if (startMinutes === endMinutes) return [];
        if (startMinutes < endMinutes) {
            return [{ start: startMinutes, end: endMinutes }];
        } else {
            return [
                { start: startMinutes, end: MAX_MINUTES_IN_DAY },
                { start: 0, end: endMinutes },
            ];
        }
    };

    const intervals1 = getIntervals(s1, e1);
    const intervals2 = getIntervals(s2, e2);

    for (const i1 of intervals1) {
        for (const i2 of intervals2) {
            if (Math.max(i1.start, i2.start) < Math.min(i1.end, i2.end)) {
                return true;
            }
        }
    }
    return false;
};
const generateOriginalIndividualTables = (
    uniqueBaseId,
    total,
    capacityValue,
    floorSpecificCleaningDuration
) => {
    const tables = [];
    for (let i = 0; i < total; i++) {
        const tableId = `${uniqueBaseId}-t${i + 1}-${Math.random()
            .toString(36)
            .substr(2, 5)}`;
        tables.push({
            id: tableId,
            seatingStatus: "Available", // We use a component-standard status here
            bookings: [],
            capacityValue: capacityValue,
            floorDefaultCleaningDuration: floorSpecificCleaningDuration,

        });
    }
    return tables;
};

// =======================================================
// ============== DATA TRANSFORMATION (MERGED) ===========
// =======================================================
const transformApiDataToFloorStructure = (apiSeatingData, apiBookingsData) => {
    if (!Array.isArray(apiSeatingData)) {
        console.error("API seating data is not an array:", apiSeatingData);
        return [];
    }

    // Create a map of bookings for efficient lookup, keyed by table code.
    const bookingsMap = new Map();
    if (Array.isArray(apiBookingsData)) {
        apiBookingsData.forEach(booking => {
            const tableCode = booking.seating?.code;
            if (tableCode) {
                if (!bookingsMap.has(tableCode)) {
                    bookingsMap.set(tableCode, []);
                }

                // Transform the raw booking object into the consistent format used by the component.
                const startTime = (booking.startTime || "").slice(0, 5);
                const duration = booking.durationMinutes;
                let endTime = (booking.endTime || "").slice(0, 5);
                if (!endTime && startTime && duration) {
                    endTime = calculateEndTimeFromDuration(startTime, duration);
                }

                const formattedBooking = {
                    id: booking.id, // The unique ID from the backend.
                    guestName: booking.guestName, // Use guestName from backend.
                    name: booking.guestName, // Also map to 'name' for consistency.
                    phoneNumber: booking.phoneNumber, // Use phoneNumber from backend.
                    phone: booking.phoneNumber, // Also map to 'phone'.
                    date: booking.bookingDate,
                    time: startTime,
                    endTime: endTime,
                    notes: booking.description,
                };
                bookingsMap.get(tableCode).push(formattedBooking);
            }
        });
    }

    const floorsMap = new Map();

    apiSeatingData.forEach((table) => {
        const floorName = table.name || "Unnamed Floor";
        const floorKey = floorName.trim().toLowerCase();
        const capacityStr = String(table.capacity || "N/A");

        if (!floorsMap.has(floorKey)) {
            floorsMap.set(floorKey, {
                id: `floor-${floorKey.replace(/\s+/g, "-")}-${Date.now() % 10000}`,
                floorName: floorName,
                configurations: [],
                isAdded: true,
                defaultCleaningDuration: 15, // Hardcoded value
            });
        }
        const floorObj = floorsMap.get(floorKey);

        let configForCapacity = floorObj.configurations.find(
            (conf) => conf.capacity === capacityStr
        );

        if (!configForCapacity) {
            configForCapacity = {
                id: `config-${floorObj.id}-cap${capacityStr}`,
                capacity: capacityStr,
                totalTables: 0,
                individualTables: [],
                parentFloorId: floorObj.id,
            };
            floorObj.configurations.push(configForCapacity);
        }

        const tableCode = table.code;
        const associatedBookings = bookingsMap.get(tableCode) || [];
        associatedBookings.sort((a, b) => (timeToMinutes(a.time) ?? 0) - (timeToMinutes(b.time) ?? 0));

        configForCapacity.individualTables.push({
            id: table.id,
            code: table.code,
            seatingStatus: table.seatingStatus,
            bookings: associatedBookings,
            capacityValue: capacityStr,
            floorDefaultCleaningDuration: floorObj.defaultCleaningDuration,
        });
    });

    floorsMap.forEach((floor) => {
        floor.configurations.forEach((config) => {
            config.totalTables = config.individualTables.length;
        });
    });

    return Array.from(floorsMap.values());
};

let nextFloorIdCounter = 1;
let nextWaitlistIdCounter = 1;
const generateWaitlistId = () =>
    `wl-${nextWaitlistIdCounter++}-${Date.now() % 10000}`;

// --- Child Components ---
const StatusLegend = () => (
    <div style={legendContainerStyle}>
        <h4 style={legendTitleStyle}>Table Status Legend:</h4>
        {tableStatuses.map((status) => (
            <div key={status} style={legendItemStyle}>
                <span
                    style={{
                        ...legendColorBoxStyle,
                        backgroundColor: statusColors[status],
                    }}
                ></span>
                {status}
            </div>
        ))}
    </div>
);
const formatReservationDetailsForDisplay = (details, includeDate = true) => {
    if (!details || typeof details !== "object") return details || "";
    let displayString = `Name: ${details.name || "N/A"}`;
    if (details.phone) displayString += `, Phone: ${details.phone}`;
    if (includeDate && details.date) displayString += `, Date: ${details.date}`;
    if (details.time && details.endTime) {
        displayString += `, Time: ${details.time} - ${details.endTime}`;
    }
    if (details.notes) displayString += `, Notes: ${details.notes}`;
    return displayString;
};
const isTableAvailableForBooking = (
    tableBookingsOnDate,
    newBookingStartTime,
    newBookingEndTime,
    cleaningDurationToUse
) => {
    if (
        !newBookingStartTime ||
        !newBookingEndTime ||
        timeToMinutes(newBookingStartTime) === null ||
        timeToMinutes(newBookingEndTime) === null
    ) {
        console.error(
            "isTableAvailableForBooking called with invalid new booking times."
        );
        return false;
    }

    for (const existingBooking of tableBookingsOnDate) {
        if (!existingBooking.time || !existingBooking.endTime) continue;

        const existingBookingActualEndTimeStr = existingBooking.endTime;
        const existingBookingBlockedUntilAfterCleaningStr =
            calculateEndTimeFromDuration(
                existingBooking.endTime,
                cleaningDurationToUse
            );

        if (!existingBookingBlockedUntilAfterCleaningStr) {
            console.warn(
                "Could not calculate end of cleaning for existing booking:",
                existingBooking
            );
            if (
                isTimeOverlap(
                    newBookingStartTime,
                    newBookingEndTime,
                    existingBooking.time,
                    existingBookingActualEndTimeStr
                )
            ) {
                return false;
            }
            continue;
        }
        if (
            isTimeOverlap(
                newBookingStartTime,
                newBookingEndTime,
                existingBooking.time,
                existingBookingBlockedUntilAfterCleaningStr
            )
        ) {
            return false;
        }
    }
    return true;
};
const getShortTableIdentifier = (tableId) => {
    if (!isNaN(tableId)) {
        return `T${tableId}`;
    }
    const parts = String(tableId).split("-t");
    if (parts.length > 1) {
        const numPart = parts[1].split("-")[0];
        return `T${numPart}`;
    }
    if (typeof tableId === 'string' && tableId.includes('_t')) {
        const codeParts = tableId.split('_t');
        if (codeParts.length > 1) {
            return `T${codeParts[1]}`;
        }
    }
    return String(tableId).slice(-6);
};
const AllBookingsView = ({
    tableHeaderStyle,
    tableCellStyle,
    evenRowStyle,
    oddRowStyle,
    paginationButtonStyle,
}) => {
    const [bookings, setBookings] = useState([]);
    const [currentBookingsPage, setCurrentBookingsPage] = useState(1);
    const bookingsPerPage = 10;
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const indexOfLastBooking = currentBookingsPage * bookingsPerPage;
    const indexOfFirstBooking = indexOfLastBooking - bookingsPerPage;
    const currentBookingsToShow = bookings.slice(
        indexOfFirstBooking,
        indexOfLastBooking
    );
    const totalBookingPages = Math.ceil(bookings.length / bookingsPerPage);

    useEffect(() => {
        if (!vendorId || !authToken) return;
        const controller = new AbortController();
        const token = localStorage.getItem("authToken");


        const fetchBookings = async () => {
            setLoading(true);
            setError("");

            try {
                const res = await fetch(
                    `https://backend.maamaas.com/food/api/seatingdetails/waiting/vendor/${vendorId}`,
                    {
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${token}`,
                        },
                        signal: controller.signal,
                    }
                );

                if (!res.ok) throw new Error(`HTTP ${res.status}`);

                const data = await res.json();
                setBookings(Array.isArray(data) ? data : []);
            } catch (err) {
                if (err.name !== "AbortError") {
                    console.error("Fetch error:", err);
                    setError("Could not load bookings.");
                }
            } finally {
                setLoading(false);
            }
        };

        fetchBookings();
        return () => controller.abort();
    }, [vendorId, authToken]);

    if (loading) return <p>Loading bookings…</p>;
    if (error) return <p style={{ color: "red" }}>{error}</p>;

    if (bookings.length === 0) {
        return (
            <div style={{ marginTop: "20px" }}>
                <h3 style={{ textAlign: "center", marginBottom: "15px", color: "#333", fontSize: "20px", fontWeight: "600" }}>
                    <FaListAlt style={{ marginRight: "8px" }} />
                    All Customer Bookings
                </h3>
                <p style={{ textAlign: "center", marginTop: "20px", fontSize: "16px", color: "#555" }}>
                    No customer bookings found.
                </p>
            </div>
        );
    }

    return (
        <>
            <style>
                {`
            .RightPad{
            padding-right:0px !important;
            }
            `}
            </style>

            <div
                style={{ marginTop: "20px", overflowX: "auto", scrollbarWidth: "thin" }}
                className="rep rep2 pe-4 RightPad"
            >
                <h3
                    style={{
                        textAlign: "center",
                        marginBottom: "15px",
                        color: "#333",
                        fontSize: "20px",
                        fontWeight: "600",
                    }}
                >
                    <FaListAlt style={{ marginRight: "8px" }} />
                    All Customer Bookings
                </h3>
                <table
                    style={{
                        width: "100%",
                        borderCollapse: "collapse",
                        fontSize: "12px",
                        height: "30vh",
                        overflow: "auto",
                    }}
                    className="table table-bordered"
                >
                    <thead>
                        <tr>
                            <th style={tableHeaderStyle}>S.No</th>
                            <th style={{ ...tableHeaderStyle }}>Date</th>
                            <th style={{ ...tableHeaderStyle }}>Time Slot</th>
                            <th style={{ ...tableHeaderStyle }}>Table ID</th>
                            <th style={{ ...tableHeaderStyle }}>Booking ID</th>
                            <th style={{ ...tableHeaderStyle }}>Floor</th>
                        </tr>
                    </thead>
                    <tbody>
                        {currentBookingsToShow.map((booking, index) => {
                            const addMinutes = (timeStr, mins) => {
                                if (!timeStr) return "";
                                const [h, m, s] = timeStr.split(":").map(Number);
                                const date = new Date(2000, 0, 1, h || 0, m || 0, s || 0);
                                date.setMinutes(date.getMinutes() + (Number(mins) || 0));
                                const hh = String(date.getHours()).padStart(2, "0");
                                const mm = String(date.getMinutes()).padStart(2, "0");
                                const ss =
                                    typeof s === "number"
                                        ? ":" + String(date.getSeconds()).padStart(2, "0")
                                        : "";
                                return `${hh}:${mm}${ss}`;
                            };

                            let startTime = (booking.startTime || booking.time || "").slice(0, 5);
                            let duration = booking.durationMinutes || booking.duration || 0;
                            let endTime = booking.endTime
                                ? booking.endTime.slice(0, 5)
                                : startTime && duration
                                    ? addMinutes(startTime, duration).slice(0, 5)
                                    : "";

                            return (
                                <tr
                                    key={booking.id}
                                    style={index % 2 === 0 ? evenRowStyle : oddRowStyle}
                                >
                                    <td style={tableCellStyle}>
                                        {indexOfFirstBooking + index + 1}
                                    </td>
                                    <td style={tableCellStyle}>{booking.bookingDate}</td>
                                    <td style={tableCellStyle}>
                                        {startTime} - {endTime}
                                    </td>
                                    <td style={tableCellStyle}>
                                        {booking.seating?.code || booking.tableShortId || ""}
                                    </td>
                                    <td style={tableCellStyle}>
                                        {booking.id || ""}
                                    </td>
                                    <td style={tableCellStyle}>
                                        {booking.seating?.name || booking.floorName || ""}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                {bookings.length > bookingsPerPage && (
                    <div
                        style={{
                            marginTop: "15px",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            paddingBottom: "10px",
                        }}
                    >
                        <button
                            onClick={() => setCurrentBookingsPage((p) => Math.max(p - 1, 1))}
                            disabled={currentBookingsPage === 1}
                            style={paginationButtonStyle}
                        >
                            Prev
                        </button>
                        <span style={{ margin: "0 10px", fontSize: "13px" }}>
                            Page {currentBookingsPage} of {totalBookingPages}
                        </span>
                        <button
                            onClick={() =>
                                setCurrentBookingsPage((p) => Math.min(p + 1, totalBookingPages))
                            }
                            disabled={currentBookingsPage === totalBookingPages}
                            style={paginationButtonStyle}
                        >
                            Next
                        </button>
                    </div>
                )}
            </div>
        </>
    );
};

const WaitlistView = ({
    waitlist,
    onSeatGuestRequest,
    onRemoveFromWaitlist,
    tableHeaderStyle,
    tableCellStyle,
    evenRowStyle,
    oddRowStyle,
    paginationButtonStyle,
    smallActionButtonStyle,
    statusColors,
}) => {
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 5;

    const sortedWaitlist = useMemo(
        () => [...waitlist].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp)),
        [waitlist]
    );

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItemsToShow = sortedWaitlist.slice(
        indexOfFirstItem,
        indexOfLastItem
    );
    const totalPages = Math.ceil(sortedWaitlist.length / itemsPerPage);

    if (waitlist.length === 0) {
        return (
            <div style={{ marginTop: "20px" }}>
                <h3 style={{ textAlign: "center", marginBottom: "15px", color: "#333", fontSize: "20px", fontWeight: "600" }}>
                    <FaUsers style={{ marginRight: "8px" }} /> Waitlist
                </h3>
                <p style={{ textAlign: "center", marginTop: "20px", fontSize: "16px", color: "#555" }}>
                    The waitlist is currently empty.
                </p>
            </div>
        );
    }

    return (
        <div style={{ marginTop: "20px", overflowX: "auto", scrollbarWidth: "thin" }} className="rep rep2 pe-4 RightPad">
            <h3 style={{ textAlign: "center", marginBottom: "15px", color: "#333", fontSize: "20px", fontWeight: "600" }}>
                <FaUsers style={{ marginRight: "8px" }} /> Waitlist
            </h3>
            <table style={{
                width: "100%", borderCollapse: "collapse", fontSize: "12px", height: "30vh",
                overflow: "auto",
            }} className="table table-bordered">
                <thead>
                    <tr>
                        <th style={tableHeaderStyle}>S.No</th>
                        <th style={tableHeaderStyle}>Name</th>
                        <th style={tableHeaderStyle}>Size</th>
                        <th style={tableHeaderStyle}>Phone</th>
                        <th style={{ ...tableHeaderStyle, whiteSpace: "wrap" }}>Duration (min)</th>
                        <th style={tableHeaderStyle}>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {currentItemsToShow.map((item, index) => (
                        <tr key={item.id || index} style={index % 2 === 0 ? evenRowStyle : oddRowStyle}>
                            <td style={tableCellStyle}>{indexOfFirstItem + index + 1}</td>
                            <td style={tableCellStyle}>{item.guestName || item.name}</td>
                            <td style={tableCellStyle}>{item.capacity || item.partySize}</td>
                            <td style={tableCellStyle}>{item.phoneNumber || item.phone || 'N/A'}</td>
                            <td style={tableCellStyle}>{item.durationMinutes || item.duration || ''}</td>
                            <td style={{ ...tableCellStyle, whiteSpace: 'nowrap' }}>
                                <button
                                    onClick={() => onRemoveFromWaitlist(item.id)}
                                    style={{ ...smallActionButtonStyle, backgroundColor: statusColors.Occupied, color: 'white' }}
                                    title="Remove from waitlist"
                                >
                                    Remove
                                </button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
            {sortedWaitlist.length > itemsPerPage && (
                <div style={{ marginTop: "15px", display: "flex", justifyContent: "center", alignItems: "center", paddingBottom: "10px" }}>
                    <button
                        onClick={() => setCurrentPage(p => Math.max(p - 1, 1))}
                        disabled={currentPage === 1}
                        style={paginationButtonStyle}
                    >
                        Prev
                    </button>
                    <span style={{ margin: "0 10px", fontSize: "13px" }}>
                        Page {currentPage} of {totalPages}
                    </span>
                    <button
                        onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))}
                        disabled={currentPage === totalPages}
                        style={paginationButtonStyle}
                    >
                        Next
                    </button>
                </div>
            )}
        </div>
    );
};


// ====================================================================
//                        MAIN COMPONENT START
// ====================================================================
const Tablereservation = () => {
    // --- STATE MANAGEMENT ---
    const [data, setData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const isLoadingRef = useRef(isLoading);
    useEffect(() => {
        isLoadingRef.current = isLoading;
    }, [isLoading]);

    const [apiError, setApiError] = useState(null);
    const [showFloorEditModal, setShowFloorEditModal] = useState(false);
    const [editingFloor, setEditingFloor] = useState(null);
    const [focusedCapacity, setFocusedCapacity] = useState(null);
    const [newConfigCapacity, setNewConfigCapacity] = useState("");
    const [newConfigNumTables, setNewConfigNumTables] = useState("");

    const [showIndividualTableModal, setShowIndividualTableModal] = useState(false);
    const [editingTableInfo, setEditingTableInfo] = useState(null);

    const [showBookTableModal, setShowBookTableModal] = useState(false);
    const [bookingTableInfo, setBookingTableInfo] = useState(null);
    const [bookingCustomerName, setBookingCustomerName] = useState("");
    const [bookingPhoneNumber, setBookingPhoneNumber] = useState("");
    const [bookingDate, setBookingDate] = useState(getTodayDate());
    const [bookingStartTime, setBookingStartTime] = useState(
        getDefaultStartTime()
    );
    const [bookingDurationMinutes, setBookingDurationMinutes] = useState("90"); // Hardcoded value
    const [bookingNotes, setBookingNotes] = useState("");
    const [globalSelectedDate, setGlobalSelectedDate] = useState(getTodayDate());
    const [globalSelectedTime, setGlobalSelectedTime] = useState(
        getDefaultStartTime()
    );
    const [selectedFilterFloorId, setSelectedFilterFloorId] = useState("");
    const [actualTimeTick, setActualTimeTick] = useState(() => new Date());
    const [waitlist, setWaitlist] = useState([]);
    const [showAddToWaitlistModal, setShowAddToWaitlistModal] = useState(false);
    const [newWaitlistEntryName, setNewWaitlistEntryName] = useState("");
    const [newWaitlistEntryPartySize, setNewWaitlistEntryPartySize] =
        useState("");
    const [newWaitlistEntryPhone, setNewWaitlistEntryPhone] = useState("");

    const [waitlistModalFloorId, setWaitlistModalFloorId] = useState("");

    const [showSeatWaitlistGuestModal, setShowSeatWaitlistGuestModal] =
        useState(false);
    const [seatingWaitlistGuestInfo, setSeatingWaitlistGuestInfo] =
        useState(null);
    const [selectedTableForWaitlistSeating, setSelectedTableForWaitlistSeating] =
        useState("");
    const [isProcessingAutoSeat, setIsProcessingAutoSeat] = useState(false);
    const [postError, setPostError] = useState(null);
    const [isPosting, setIsPosting] = useState(false);
    const [id, setId] = useState();

    const fetchData = useCallback(async () => {
        setIsLoading(true);
        setApiError(null);

        const token = authToken || localStorage.getItem("authToken");

        if (!token) {
            setApiError("Authentication token is missing. Please log in again.");
            setIsLoading(false);
            return;
        }

        try {
            const urls = {
                seating: `https://backend.maamaas.com/food/api/seating/all/vendor/${vendorId}`,
                bookings: `https://backend.maamaas.com/food/api/seatingdetails/waiting/vendor/${vendorId}`,
            };


            const [seatingResponse, bookingsResponse] = await Promise.all([
                fetch(urls.seating, {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                }),
                fetch(urls.bookings, {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                }),
            ]);

            if (!seatingResponse.ok) {
                const errorText = await seatingResponse.text();
                throw new Error(
                    `Seating API Error. Status: ${seatingResponse.status}. Body: ${errorText}`
                );
            }

            if (!bookingsResponse.ok) {
                const errorText = await bookingsResponse.text();
                throw new Error(
                    `Bookings API Error. Status: ${bookingsResponse.status}. Body: ${errorText}`
                );
            }

            const apiSeatingData = await seatingResponse.json();
            const apiBookingsData = await bookingsResponse.json();

            const transformedData = transformApiDataToFloorStructure(
                apiSeatingData,
                apiBookingsData
            );

            setData(transformedData);
            nextFloorIdCounter = transformedData.length + 1;
        } catch (error) {
            console.error("Error fetching or transforming data:", error);
            setApiError(error.message);
        } finally {
            setIsLoading(false);
        }
    }, [authToken, vendorId]);


    const fetchWaitlist = useCallback(async () => {
        if (!authToken || !vendorId) return;
        try {
            const response = await fetch(
                `https://backend.maamaas.com/food/api/seatingdetails/waiting/vendor/${vendorId}`,
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${authToken}`,
                    },
                }
            );
            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Waitlist fetch failed: ${response.status} - ${errorText}`);
            }
            const data = await response.json();
            setWaitlist(Array.isArray(data) ? data : []);
        } catch (error) {
            // console.error("Error fetching waitlist:", error.message);
        }
    }, [authToken, vendorId]);

    useEffect(() => {
        if (vendorId && authToken) {
            fetchData();
            fetchWaitlist();
        }
    }, [fetchData, fetchWaitlist, vendorId, authToken]);


    // ============================================================
    // POLLING FIX: Replaced old useEffect with useInterval
    // This will now correctly poll for data every 30 seconds
    // ============================================================
    const POLLING_RATE = 30000; // 30 seconds

    useInterval(() => {
        if (!isLoadingRef.current) {
            fetchData();
        }
    }, POLLING_RATE);

    useInterval(() => {
        if (!isLoadingRef.current) {
            fetchWaitlist();
        }
    }, POLLING_RATE);


    useEffect(() => {
        const timerId = setInterval(() => setActualTimeTick(new Date()), 60000);
        return () => clearInterval(timerId);
    }, []);

    const actualCurrentDate = useMemo(() => {
        const d = actualTimeTick;
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
            2,
            "0"
        )}-${String(d.getDate()).padStart(2, "0")}`;
    }, [actualTimeTick]);

    const actualCurrentTime = useMemo(() => {
        const d = actualTimeTick;
        return `${String(d.getHours()).padStart(2, "0")}:${String(
            d.getMinutes()
        ).padStart(2, "0")}`;
    }, [actualTimeTick]);

    const confirmAndSeatGuest = useCallback(async (guestDetails, tableDetails) => {
        const walkInStartTime = actualCurrentTime;
        const walkInEndTime = calculateEndTimeFromDuration(walkInStartTime, 90); // Hardcoded value

        if (!walkInEndTime) {
            console.error("Seating failed: Could not determine seating end time.");
            return false;
        }

        const floorObj = data.find(f => f.id === tableDetails.floorId);
        const configObj = floorObj?.configurations.find(c => c.id === tableDetails.configId);
        const tableObj = configObj?.individualTables.find(t => t.id === tableDetails.id);

        if (!tableObj || !floorObj) {
            console.error("Seating failed: Could not find table/floor objects in local data.");
            return false;
        }

        // Check 1: Conflict with existing reservations
        const bookingsOnDate = (tableObj.bookings || []).filter(b => b.date === actualCurrentDate);
        const isTrulyAvailable = isTableAvailableForBooking(bookingsOnDate, walkInStartTime, walkInEndTime, floorObj.defaultCleaningDuration);

        if (!isTrulyAvailable) {
            console.warn(`Seating conflict for table ${tableObj.code}. A walk-in would clash with another booking.`);
            return false;
        }

        // Check 2: Restaurant operating hours
        const seatingStartActualDateTime = new Date(`${actualCurrentDate}T${walkInStartTime}`);
        const seatingEndActualDateTime = new Date(seatingStartActualDateTime.getTime() + 90 * 60000); // Hardcoded value
        const opWindowStartForSeatingDate = new Date(`${actualCurrentDate}T${RESTAURANT_OPEN_TIME_STR}`);
        let opWindowEndForSeatingDate = new Date(`${actualCurrentDate}T${RESTAURANT_CLOSE_TIME_STR}`);
        if (timeToMinutes(RESTAURANT_CLOSE_TIME_STR) < timeToMinutes(RESTAURANT_OPEN_TIME_STR)) {
            opWindowEndForSeatingDate.setDate(opWindowEndForSeatingDate.getDate() + 1);
        }

        if (seatingStartActualDateTime < opWindowStartForSeatingDate || seatingEndActualDateTime > opWindowEndForSeatingDate) {
            console.warn(`Seating attempt for ${guestDetails.guestName} failed: falls outside of restaurant operating hours.`);
            return false;
        }

        // All checks passed. Proceed with API calls.
        const formatTimeToHHMMSS = (timeStr) => `${timeStr.length === 5 ? timeStr : "00:00"}:00`;
        const finalPayload = {
            startTime: formatTimeToHHMMSS(walkInStartTime),
            durationMinutes: 90, // Hardcoded value
            phoneNumber: guestDetails.phoneNumber || null,
            guestName: guestDetails.guestName,
            description: `Seated from waitlist. Party of ${guestDetails.capacity}.`,
            seating: {
                name: floorObj.floorName,
                seatingStatus: 'Occupied',
                code: tableObj.code,
                capacity: parseInt(tableObj.capacityValue, 10) || 0,
            },
        };

        try {
            // Step A: Create the booking
            const bookingUrl = `https://backend.maamaas.com/food/api/seatingdetails/vendor/${vendorId}`;
            const bookingRes = await fetch(bookingUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
                body: JSON.stringify(finalPayload),
            });
            if (!bookingRes.ok) {
                const errorText = await bookingRes.text();
                throw new Error(`Booking creation API failed: ${errorText}`);
            }

            // Step B: Remove the guest from the waitlist
            const waitlistId = guestDetails.id;
            const deleteUrl = `https://backend.maamaas.com/food/api/seatingdetails/delete/waiting-list/${waitlistId}`;
            const deleteRes = await fetch(deleteUrl, {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
            });
            if (!deleteRes.ok) {
                const errorText = await deleteRes.text();
                console.warn(`Booking created successfully, but failed to remove guest ${waitlistId} from waitlist: ${errorText}`);
            }

            return true; // API calls succeeded
        } catch (error) {
            console.error("Error during seating guest API calls:", error);
            // alert(`Could not seat guest. An API error occurred: ${error.message}`);
            return false;
        }
    }, [actualCurrentTime, actualCurrentDate, data, authToken, vendorId]);

    useEffect(() => {
        // This effect tries to automatically seat guests from the waitlist
        if (isProcessingAutoSeat || showBookTableModal || showIndividualTableModal || showFloorEditModal || showAddToWaitlistModal || showSeatWaitlistGuestModal) {
            return; // Do nothing if a modal is open or already processing
        }

        const attemptAutoSeating = async () => {
            const waitingGuests = waitlist
                .filter((g) => g.status === "waiting" || !g.status) // Handle null status
                .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

            if (!waitingGuests.length) return;

            const allCurrentlyAvailableTables = [];
            data.forEach((floor) => {
                floor.configurations.forEach((config) => {
                    config.individualTables.forEach((table) => {
                        if (getDisplayStatus(table.seatingStatus) === "Available") {
                            allCurrentlyAvailableTables.push({
                                id: table.id,
                                code: table.code,
                                capacityValue: table.capacityValue,
                                floorId: floor.id,
                                configId: config.id,
                            });
                        }
                    });
                });
            });

            if (!allCurrentlyAvailableTables.length) return;

            setIsProcessingAutoSeat(true); // Set lock to prevent other processes
            let availableTablesForThisCycle = [...allCurrentlyAvailableTables];
            let guestsWereSeated = false;

            for (const guest of waitingGuests) {
                if (availableTablesForThisCycle.length === 0) break;

                const perfectFitTableIndex = availableTablesForThisCycle.findIndex(
                    (table) => parseInt(table.capacityValue, 10) === guest.capacity
                );

                if (perfectFitTableIndex !== -1) {
                    const tableToAssign = availableTablesForThisCycle[perfectFitTableIndex];
                    const success = await confirmAndSeatGuest(guest, tableToAssign);

                    if (success) {
                        guestsWereSeated = true;
                        availableTablesForThisCycle.splice(perfectFitTableIndex, 1);
                    }
                }
            }

            if (guestsWereSeated) {
                alert('One or more guests from the waitlist have been automatically seated. The view will now refresh.');
                await fetchData();
                await fetchWaitlist();
            }

            setIsProcessingAutoSeat(false); // Release lock
        };

        attemptAutoSeating();

    }, [data, waitlist, confirmAndSeatGuest, fetchData, fetchWaitlist, showBookTableModal, showIndividualTableModal, showFloorEditModal, showAddToWaitlistModal, showSeatWaitlistGuestModal]);

    const processedDataForDisplay = useMemo(() => {
        let sourceFloors = [...data];
        if (selectedFilterFloorId && selectedFilterFloorId !== "") {
            sourceFloors = sourceFloors.filter(
                (floor) => floor.id === selectedFilterFloorId
            );
        }
        const expandedRows = [];
        sourceFloors.forEach((floor) => {
            const configsByCapacity = new Map();
            floor.configurations.forEach((config) => {
                config.individualTables = config.individualTables.map((table) => ({
                    ...table,
                    id: table.id || table.code || `table-${Math.random().toString(36).substr(2, 8)}`,
                }));
                if (!configsByCapacity.has(config.capacity))
                    configsByCapacity.set(config.capacity, []);
                configsByCapacity.get(config.capacity).push(config);
            });
            if (configsByCapacity.size === 0) {
                expandedRows.push({
                    uniqueRowId: `${floor.id}-no_config_placeholder`,
                    floor,
                    capacity: "N/A",
                    configurations: [],
                    isPlaceholder: true,
                });
            } else {
                const sortedCapacities = Array.from(configsByCapacity.keys()).sort(
                    (a, b) => parseInt(a) - parseInt(b)
                );
                sortedCapacities.forEach((capacity) => {
                    expandedRows.push({
                        uniqueRowId: `${floor.id}-cap-${capacity}`,
                        floor,
                        capacity,
                        configurations: configsByCapacity.get(capacity),
                        isPlaceholder: false,
                    });
                });
            }
        });
        return expandedRows;
    }, [data, selectedFilterFloorId]);

    const getRowTableStats = useCallback((rowItem) => {
        let total = 0;
        let available = 0;
        rowItem.configurations.forEach((config) => {
            const tableCount = config.individualTables.length;
            total += tableCount;
            config.individualTables.forEach((t) => {
                const status = getComputedTableStatus(t, globalSelectedDate, globalSelectedTime, actualCurrentDate, actualCurrentTime);
                if (status === "Available") {
                    available++;
                }
            });
        });
        return { total, available };
    }, [globalSelectedDate, globalSelectedTime, actualCurrentDate, actualCurrentTime]);

    const handleAddFloorRow = () => {
        const newFloorGeneratedId = `new-floor-${nextFloorIdCounter++}-${Date.now() % 1000
            }`;
        const newFloor = {
            id: newFloorGeneratedId,
            floorName: `New Floor ${nextFloorIdCounter - 1}`,
            configurations: [],
            isAdded: false,
            defaultCleaningDuration: 15, // Hardcoded value
        };
        setEditingFloor(newFloor);
        setFocusedCapacity(null);
        setNewConfigCapacity("");
        setNewConfigNumTables("");
        setShowFloorEditModal(true);
    };

    const handleEditFloorSetup = (floorId, capacityContext) => {
        const floorToEdit = data.find((f) => f.id === floorId);
        if (floorToEdit) {
            const floorWithDefaults = {
                ...floorToEdit,
                defaultCleaningDuration:
                    floorToEdit.defaultCleaningDuration !== undefined
                        ? floorToEdit.defaultCleaningDuration
                        : 15, // Hardcoded value
            };
            setEditingFloor(JSON.parse(JSON.stringify(floorWithDefaults)));
            setFocusedCapacity(capacityContext);
            setNewConfigCapacity("");
            setNewConfigNumTables("");
            setShowFloorEditModal(true);
        }
    };

    /**
     * [REFACTORED FUNCTION]
     * Handles adding a new table configuration (e.g., 5 tables of capacity 4).
     * It now makes a single API call and then refetches all data to ensure consistency,
     * removing the old "static" client-side table generation.
     */
    const handleAddConfigurationToEditingFloor = async () => {
        if (!editingFloor) return;

        // Extra safeguard for vendorId, in case of unusual state issues.
        if (!vendorId) {
            alert("Critical Error: Vendor ID is missing. Cannot proceed. Please log in again.");
            return;
        }

        const capacity = newConfigCapacity.trim();
        const numTables = parseInt(newConfigNumTables, 10);

        if (!capacity || isNaN(parseInt(capacity, 10)) || parseInt(capacity, 10) <= 0) {
            alert("A valid, positive number for capacity is required.");
            return;
        }
        if (isNaN(numTables) || numTables <= 0) {
            alert("A valid, positive number of tables is required.");
            return;
        }
        if (editingFloor.configurations.some((conf) => conf.capacity === capacity)) {
            alert(`A configuration for capacity '${capacity}' already exists on this floor.`);
            return;
        }

        setIsPosting(true);
        setPostError(null);

        try {
            const url = `https://backend.maamaas.com/food/api/seating/vendor/${vendorId}`;
            const payload = {
                numberOfTables: numTables,
                capacityPerTable: parseInt(capacity, 10),
                name: editingFloor.floorName,
            };

            const res = await fetch(url, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${authToken}`,
                },
                body: JSON.stringify(payload),
            });

            if (!res.ok) {
                const errorText = await res.text();
                // This is likely where the "vendor id not found" error message originates.
                throw new Error(`API Error (${res.status}): ${errorText || 'Failed to create tables.'}`);
            }

            // SUCCESS
            alert("New table configuration has been saved successfully. The view will now refresh.");

            // Close the modal and reset state
            setShowFloorEditModal(false);
            setEditingFloor(null);

            // REFETCH data from the server. This is the single source of truth.
            // This robustly replaces the old method of adding "static" tables to the UI.
            await fetchData();

        } catch (err) {
            console.error("Error during table configuration creation:", err);
            setPostError(err.message);
            alert(`An error occurred while creating tables: ${err.message}`);
        } finally {
            setIsPosting(false);
        }
    };

    const handleRemoveConfigurationFromEditingFloor = (configIdToRemove) => {
        if (
            !editingFloor ||
            !window.confirm(
                "Remove this entire table configuration group (all tables of this capacity)?"
            )
        )
            return;
        setEditingFloor((prev) => ({
            ...prev,
            configurations: prev.configurations.filter(
                (c) => c.id !== configIdToRemove
            ),
        }));
    };

    const handleRemoveIndividualTableFromEditingFloorConfig = async (
        configId,
        tableIdToRemove
    ) => {
        if (
            !editingFloor ||
            !window.confirm(
                "Are you sure you want to permanently delete this table? This action cannot be undone."
            )
        )
            return;

        try {
            const response = await fetch(
                `https://backend.maamaas.com/food/api/seating/delete/${tableIdToRemove}`,
                {
                    method: "DELETE",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${authToken}`,
                    },
                }
            );

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Failed to delete seating. Server says: ${errorText}`);
            }

            alert("Table deleted successfully! The view will now refresh.");

            setShowFloorEditModal(false);
            setEditingFloor(null);
            await fetchData();

        } catch (error) {
            console.error("Delete error:", error);
            alert(error.message || "Failed to delete seating. Please check the console and try again.");
        }
    };


    const handleAddIndividualTableToEditingFloorConfig = (configId) => {
        if (!editingFloor) return;
        setEditingFloor((prev) => {
            if (!prev) return null;
            const updatedConfigurations = prev.configurations.map((config) => {
                if (config.id === configId) {
                    let maxTNum = 0;
                    config.individualTables.forEach((t) => {
                        const match = String(t.id).match(/-t(\d+)-/);
                        if (match && parseInt(match[1]) > maxTNum) {
                            maxTNum = parseInt(match[1]);
                        }
                    });
                    const nextTNum = config.individualTables.length > 0 ? maxTNum + 1 : 1;

                    const newTableId = `${config.id}-t${nextTNum}-${Math.random()
                        .toString(36)
                        .substr(2, 5)}`;
                    const newTable = {
                        id: newTableId,
                        seatingStatus: "Available",
                        bookings: [],
                        capacityValue: config.capacity,
                        floorDefaultCleaningDuration:
                            prev.defaultCleaningDuration !== undefined
                                ? prev.defaultCleaningDuration
                                : 15, // Hardcoded value
                    };
                    const newIndividualTables = [...config.individualTables, newTable];
                    return {
                        ...config,
                        individualTables: newIndividualTables,
                        totalTables: newIndividualTables.length,
                    };
                }
                return config;
            });
            return { ...prev, configurations: updatedConfigurations };
        });
    };

    const submitFloorEdit = () => {
        if (!editingFloor || !editingFloor.floorName.trim()) {
            alert("Floor name is required.");
            return;
        }

        const cleanDurValue = editingFloor.defaultCleaningDuration;
        const cleanDur =
            cleanDurValue !== undefined &&
                !isNaN(parseInt(cleanDurValue, 10)) &&
                parseInt(cleanDurValue, 10) >= 0
                ? parseInt(cleanDurValue, 10)
                : 15; // Hardcoded value

        if (isNaN(cleanDur) || cleanDur < 0) {
            alert("Valid Cleaning Duration (minutes) is required for the floor.");
            return;
        }

        const floorToSave = {
            ...editingFloor,
            isAdded: true,
            defaultCleaningDuration: cleanDur,
        };
        floorToSave.configurations = floorToSave.configurations.map((config) => ({
            ...config,
            totalTables: config.individualTables.length,
            individualTables: config.individualTables.map((it) => ({
                ...it,
                floorDefaultCleaningDuration: cleanDur,
            })),
        }));

        setData((prev) => {
            const idx = prev.findIndex((f) => f.id === floorToSave.id);
            if (idx > -1) {
                const nd = [...prev];
                nd[idx] = floorToSave;
                return nd.sort((a, b) => a.floorName.localeCompare(b.floorName));
            } else {
                return [floorToSave, ...prev].sort((a, b) =>
                    a.floorName.localeCompare(b.floorName)
                );
            }
        });
        setShowFloorEditModal(false);
        setEditingFloor(null);
        setFocusedCapacity(null);
    };

    const handleIndividualTableClick = (floorId, configId, tableClicked) => {
        const displayStatus = getComputedTableStatus(tableClicked, globalSelectedDate, globalSelectedTime, actualCurrentDate, actualCurrentTime);

        if (displayStatus === "Available") {
            setBookingTableInfo({
                floorId,
                configId,
                tableId: tableClicked.id,
                tableCode: tableClicked.code,
                capacity: tableClicked.capacityValue,
            });
            setBookingCustomerName("");
            setBookingPhoneNumber("");
            setBookingDate(globalSelectedDate);
            setBookingStartTime(
                globalSelectedTime && timeToMinutes(globalSelectedTime) !== null
                    ? globalSelectedTime
                    : getDefaultStartTime()
            );
            setBookingDurationMinutes("90"); // Hardcoded value
            setBookingNotes("");
            setShowBookTableModal(true);
        } else {
            // The management modal should always show real-time data
            setEditingTableInfo({
                id,
                floorId,
                configId,
                tableId: tableClicked.id,
                capacity: tableClicked.capacityValue,
                currentStatus: tableClicked.seatingStatus, // Real-time status for the modal
                newStatus: getDisplayStatus(tableClicked.seatingStatus),
            });
            setShowIndividualTableModal(true);
        }
    };

    const handleConfirmBooking = async () => {
        if (!bookingTableInfo) return;

        const duration = parseInt(bookingDurationMinutes, 10);
        const finalStartTime = bookingStartTime;
        const finalEndTime = calculateEndTimeFromDuration(finalStartTime, duration);

        if (
            !bookingCustomerName.trim() || !bookingDate || !finalStartTime ||
            !finalEndTime || isNaN(duration) || duration <= 0
        ) {
            alert("Please fill all required booking fields with valid values.");
            return;
        }

        const { floorId, configId, tableId, tableCode } = bookingTableInfo;
        const floorObj = data.find((f) => f.id === floorId);
        const configObj = floorObj?.configurations.find((c) => c.id === configId);
        const tableObj = configObj?.individualTables.find((t) => t.id === tableId);

        if (!tableObj || !floorObj) {
            alert("Could not find table or floor information. The layout may have changed.");
            return;
        }

        const bookingsOnDate = tableObj.bookings.filter((b) => b.date === bookingDate);
        if (!isTableAvailableForBooking(bookingsOnDate, finalStartTime, finalEndTime, floorObj.defaultCleaningDuration)) {
            alert("This table is not available for the selected time slot. It may conflict with another booking.");
            return;
        }

        const formatTimeToHHMMSS = (timeStr) => `${timeStr.length === 5 ? timeStr : "00:00"}:00`;
        const backendPayload = {
            startTime: formatTimeToHHMMSS(finalStartTime),
            durationMinutes: duration,
            phoneNumber: bookingPhoneNumber || null,
            guestName: bookingCustomerName || null,
            description: bookingNotes || "",
            seating: {
                name: floorObj.floorName,
                seatingStatus: "Reserved",
                code: tableCode,
                capacity: parseInt(configObj.capacity, 10) || 0,
            },
        };

        const backendBookingUrl = `https://backend.maamaas.com/food/api/seatingdetails/vendor/${vendorId}`;
        try {
            const response = await fetch(backendBookingUrl, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${authToken}`,
                },
                body: JSON.stringify(backendPayload),
            });

            if (!response.ok) {
                const errorMsg = await response.text();
                console.error("Backend booking failed:", errorMsg);
                alert(`Failed to save booking to backend. The server responded with: ${errorMsg}`);
                return;
            }

            alert("Booking confirmed and saved successfully!");
            setShowBookTableModal(false);
            setBookingTableInfo(null);
            await fetchData();

        } catch (err) {
            console.error("Error posting to backend:", err);
            alert("A network or server error occurred while saving the booking. Please check your connection and try again.");
        }
    };

    const handleIndividualTableStatusChange = (e) =>
        setEditingTableInfo((prev) => ({ ...prev, newStatus: e.target.value }));

    const submitIndividualTableStatus = async () => {
        if (!editingTableInfo) return;

        const { tableId, newStatus, capacity, floorId } = editingTableInfo;

        const floorOfTable = data.find(f => f.id === floorId);
        if (!floorOfTable) {
            console.error("Could not find floor for the table being edited.");
            alert("An internal error occurred: floor not found.");
            return;
        }

        const floorObj = data.find(f => f.id === floorId);
        const configObj = floorObj?.configurations.find(c => c.id === editingTableInfo.configId);
        const tableObj = configObj?.individualTables.find(t => t.id === tableId);

        let seatingDetailsId = null;
        if (tableObj && Array.isArray(tableObj.bookings)) {
            const todayBookings = tableObj.bookings.filter(b => b.date === actualCurrentDate);
            if (todayBookings.length > 0) {
                seatingDetailsId = todayBookings[todayBookings.length - 1].id;
            }
        }

        if (!seatingDetailsId) {
            alert("No booking found for this table today. Cannot update seating status.");
            return;
        }

        try {
            const response = await fetch(
                `https://backend.maamaas.com/food/api/seatingdetails/vendor/seating-details/${seatingDetailsId}`,
                {
                    method: "PUT",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${authToken}`,
                    },
                    body: JSON.stringify({
                        seatingStatus: newStatus,
                    }),
                }
            );

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`API Error: ${response.status} - ${errorText}`);
            }

            setShowIndividualTableModal(false);
            setEditingTableInfo(null);
            await fetchData();
            alert("Table status updated successfully!");
        } catch (error) {
            console.error("Error updating table status on backend:", error);
            alert(
                `Failed to save table status change. Please try again. Error: ${error.message}`
            );
        }
    };

    const handleAddWaitlistGuestClick = () => {
        setNewWaitlistEntryName("");
        setNewWaitlistEntryPartySize("");
        setNewWaitlistEntryPhone("");
        setWaitlistModalFloorId(selectedFilterFloorId);
        setShowAddToWaitlistModal(true);
    };

    const handleSaveWaitlistEntry = async () => {
        const partySizeNum = parseInt(newWaitlistEntryPartySize, 10);
        if (!newWaitlistEntryName.trim()) {
            alert("Guest name is required for the waitlist.");
            return;
        }
        if (isNaN(partySizeNum) || partySizeNum <= 0) {
            alert("A valid party size is required.");
            return;
        }
        if (!waitlistModalFloorId) {
            alert("Please select a floor for the waitlist entry.");
            return;
        }
        if (!bookingDurationMinutes || parseInt(bookingDurationMinutes, 10) <= 0) {
            alert("A valid duration is required.");
            return;
        }

        const selectedFloor = data.find((floor) => floor.id === waitlistModalFloorId);
        if (!selectedFloor) {
            alert("Internal error: Could not find the selected floor. Please close and try again.");
            return;
        }

        const payload = {
            id: 0,
            floorName: selectedFloor.floorName,
            phoneNumber: newWaitlistEntryPhone || "",
            capacity: partySizeNum,
            durationMinutes: parseInt(bookingDurationMinutes),
            guestName: newWaitlistEntryName,
        };

        try {
            const response = await fetch(
                `https://backend.maamaas.com/food/api/seatingdetails/waiting-list/addBy/${vendorId}`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${authToken}`,
                    },
                    body: JSON.stringify(payload),
                }
            );

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(errorText || `Failed to add to waitlist. Status: ${response.status}`);
            }

            await fetchWaitlist(); // Refetch waitlist to show the new entry

            alert("Guest added to waitlist successfully!");
            setShowAddToWaitlistModal(false);
        } catch (err) {
            console.error("Error adding to waitlist:", err);
            alert(`Failed to add guest to waitlist: ${err.message}`);
        }
    };

    const handleRemoveFromWaitlist = async (waitlistEntryId) => {
        if (!waitlistEntryId) {
            alert("Cannot remove entry: missing ID.");
            return;
        }
        if (
            window.confirm(
                "Are you sure you want to remove this guest from the waitlist? This action is permanent."
            )
        ) {
            const deleteUrl = `https://backend.maamaas.com/food/api/seatingdetails/delete/waiting-list/${waitlistEntryId}`;
            try {
                const response = await fetch(deleteUrl, {
                    method: 'DELETE',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${authToken}`,
                    },
                }
                );
                if (!response.ok) {
                    const errorText = await response.text();
                    throw new Error(`API Delete Failed: ${response.status} - ${errorText}`);
                }
                await fetchWaitlist(); // Refetch the waitlist after successful deletion
                alert("Guest removed from waitlist successfully.");
            } catch (error) {
                console.error("Error removing from waitlist:", error);
                alert(`Could not remove guest from waitlist. Error: ${error.message}`);
            }
        }
    };


    const handleSeatGuestRequest = (waitlistEntryId) => {
        const entryToSeat = waitlist.find((e) => e.id === waitlistEntryId);
        if (entryToSeat) {
            setSeatingWaitlistGuestInfo({ entry: entryToSeat });
            setSelectedTableForWaitlistSeating("");
            setShowSeatWaitlistGuestModal(true);
        } else {
            console.error(`Waitlist entry with ID ${waitlistEntryId} not found.`);
            alert("Could not find the selected guest in the waitlist.");
        }
    };

    const availableTablesForWaitlistGuest = useMemo(() => {
        if (!seatingWaitlistGuestInfo) return [];
        const guestPartySize = seatingWaitlistGuestInfo.entry.capacity || seatingWaitlistGuestInfo.entry.partySize;
        const tables = [];
        data.forEach((floor) => {
            floor.configurations.forEach((config) => {
                config.individualTables.forEach((table) => {
                    if (parseInt(table.capacityValue) >= guestPartySize) {
                        const status = getDisplayStatus(table.seatingStatus);
                        if (status === "Available") {
                            tables.push({
                                id: table.id,
                                code: table.code,
                                shortId: getShortTableIdentifier(table.code),
                                capacityValue: table.capacityValue,
                                floorName: floor.floorName,
                                floorId: floor.id,
                                configId: config.id,
                            });
                        }
                    }
                });
            });
        });
        return tables.sort(
            (a, b) =>
                parseInt(a.capacityValue) - parseInt(b.capacityValue) ||
                a.shortId.localeCompare(b.shortId)
        );
    }, [data, seatingWaitlistGuestInfo]);

    const handleConfirmSeatWaitlistGuest = async () => {
        if (!seatingWaitlistGuestInfo || !selectedTableForWaitlistSeating) {
            alert("Please select a guest and a table.");
            return;
        }
        const guestEntry = seatingWaitlistGuestInfo.entry;
        const tableInfo = availableTablesForWaitlistGuest.find(
            (t) => t.id === selectedTableForWaitlistSeating
        );

        if (!tableInfo) {
            alert(
                "Selected table information not found or table is no longer available."
            );
            return;
        }

        const success = await confirmAndSeatGuest(guestEntry, tableInfo);

        if (success) {
            alert(
                `${guestEntry.guestName} has been seated at table ${tableInfo.shortId}.`
            );
            setShowSeatWaitlistGuestModal(false);
            setSeatingWaitlistGuestInfo(null);
            setSelectedTableForWaitlistSeating("");
            await fetchData();
            await fetchWaitlist();
        }
        // Error case is handled by an alert inside confirmAndSeatGuest
    };

    // --- RENDER LOGIC ---
    if (isLoading && data.length === 0) { // Only show full-page loader on initial load
        return (
            <div
                style={{
                    padding: "50px",
                    textAlign: "center",
                    fontSize: "20px",
                    color: "#555",
                }}
            >
                Loading Table Data...
            </div>
        );
    }

    if (apiError) {
        return (
            <div
                style={{
                    padding: "50px",
                    textAlign: "center",
                    fontSize: "20px",
                    color: "red",
                }}
            >
                Error fetching data: {apiError}
            </div>
        );
    }

    //     return (
    //         <div className="container-fluid">
    //             <style>
    //                 {`
    //                 @media screen and (max-width: 768px) {
    //     .rows {
    //         margin-top: 0px !important;
    //     }
    // }
    //                 `}
    //             </style>


    //             <div className="row rows">
    //                 <div className="col-lg-8 col-md-8 col-sm-12 col-12">
    //                     <div style={pageContainerStyle}>
    //                         <div style={mainTableSectionStyle}>
    //                             <div style={globalControlsContainerStyle} className="obr">
    //                                 <div style={globalControlItemStyle}>
    //                                     <label htmlFor="globalDate" style={labelStyle}>
    //                                         <FaCalendarAlt /> Date:
    //                                     </label>
    //                                     <input
    //                                         type="date"
    //                                         id="globalDate"
    //                                         value={globalSelectedDate}
    //                                         onChange={(e) => setGlobalSelectedDate(e.target.value)}
    //                                         style={{
    //                                             ...inputFieldStyle,
    //                                             width: "auto",
    //                                             padding: "6px 8px",
    //                                         }}
    //                                     />
    //                                 </div>
    //                                 <div style={globalControlItemStyle}>
    //                                     <label htmlFor="globalTime" style={labelStyle}>
    //                                         <FaClock /> Time:
    //                                     </label>
    //                                     <input
    //                                         type="time"
    //                                         id="globalTime"
    //                                         value={globalSelectedTime}
    //                                         onChange={(e) =>
    //                                             setGlobalSelectedTime(e.target.value || "")
    //                                         }
    //                                         style={{
    //                                             ...inputFieldStyle,
    //                                             width: "auto",
    //                                             padding: "6px 8px",
    //                                         }}
    //                                     />
    //                                 </div>
    //                                 <div style={globalControlItemStyle}>
    //                                     <label htmlFor="filterFloor" style={labelStyle}>
    //                                         <FaLayerGroup style={{ marginRight: "3px" }} /> Floor:
    //                                     </label>
    //                                     <select
    //                                         id="filterFloor"
    //                                         value={selectedFilterFloorId}
    //                                         onChange={(e) => setSelectedFilterFloorId(e.target.value)}
    //                                         style={{
    //                                             ...inputFieldStyle,
    //                                             width: "150px",
    //                                             padding: "6px 8px",
    //                                         }}
    //                                     >
    //                                         <option value="">All Floors</option>
    //                                         {data
    //                                             .sort((a, b) => a.floorName.localeCompare(b.floorName))
    //                                             .map((floor) => (
    //                                                 <option key={floor.id} value={floor.id}>
    //                                                     {floor.floorName}
    //                                                 </option>
    //                                             ))}
    //                                     </select>
    //                                 </div>
    //                                 <button
    //                                     onClick={handleAddWaitlistGuestClick}
    //                                     style={{
    //                                         ...buttonStyle,
    //                                         backgroundColor: "#17a2b8",
    //                                         marginLeft: "10px",
    //                                     }}
    //                                     title="Add guest to waitlist"
    //                                 >
    //                                     <FaUserPlus style={{ marginRight: "5px" }} /> Add to Waitlist
    //                                 </button>
    //                                 <button
    //                                     onClick={handleAddFloorRow}
    //                                     style={{
    //                                         ...buttonStyle,
    //                                         fontWeight: "bold",
    //                                         marginLeft: "auto",
    //                                     }}
    //                                     title="Add a new floor"
    //                                 >
    //                                     <FaPlus style={{ marginRight: "3px" }} />
    //                                     Add Floor
    //                                 </button>
    //                             </div>
    //                             <StatusLegend />

    //                             <div
    //                                 style={{
    //                                     marginTop: "10px",
    //                                     flexGrow: 1,
    //                                     overflowY: "auto",
    //                                     // minHeight: "100vh",
    //                                     height: "48vh",
    //                                     scrollbarWidth: "thin",

    //                                 }}
    //                                 className="rep"
    //                             >
    //                                 {processedDataForDisplay.length === 0 ? (
    //                                     <p
    //                                         style={{
    //                                             textAlign: "center",
    //                                             marginTop: "20px",
    //                                             fontSize: "16px",
    //                                         }}
    //                                     >
    //                                         {data.length === 0
    //                                             ? "No floors available from API. Click 'Add Floor' to create one."
    //                                             : "No tables match the current filter criteria."}
    //                                     </p>
    //                                 ) : (
    //                                     <>
    //                                         <div style={{}} className="rep2 pe-2">
    //                                             <table
    //                                                 style={{ width: "100%", borderCollapse: "collapse" }}
    //                                                 className="table"
    //                                             >
    //                                                 <thead>
    //                                                     <tr>
    //                                                         <th style={tableHeaderStyle}>S.No</th>
    //                                                         <th style={tableHeaderStyle}>Floor Name</th>
    //                                                         <th style={{ ...tableHeaderStyle, width: "80px" }}>
    //                                                             Capacity
    //                                                         </th>
    //                                                         <th style={{ ...tableHeaderStyle, width: "100px" }}>
    //                                                             Total Tables
    //                                                         </th>
    //                                                         <th style={{ ...tableHeaderStyle, width: "120px" }}>
    //                                                             Available
    //                                                         </th>
    //                                                         <th
    //                                                             style={{ ...tableHeaderStyle, minWidth: "250px" }}
    //                                                         >
    //                                                             Tables Status & Actions
    //                                                         </th>
    //                                                         <th style={{ ...tableHeaderStyle, width: "100px" }}>
    //                                                             Floor Actions
    //                                                         </th>
    //                                                         <th style={{ ...tableHeaderStyle, width: "100px" }}>
    //                                                             tableId
    //                                                         </th>
    //                                                     </tr>
    //                                                 </thead>
    //                                                 <tbody>
    //                                                     {processedDataForDisplay.map((rowItem, index) => {
    //                                                         const stats = getRowTableStats(rowItem);
    //                                                         return (
    //                                                             <tr
    //                                                                 key={rowItem.uniqueRowId}
    //                                                                 style={index % 2 === 0 ? evenRowStyle : oddRowStyle}
    //                                                             >
    //                                                                 <td style={tableCellStyle}>{index + 1}</td>
    //                                                                 <td style={tableCellStyle}>
    //                                                                     {rowItem.floor.floorName}
    //                                                                 </td>
    //                                                                 <td style={tableCellStyle}>
    //                                                                     {rowItem.capacity}
    //                                                                 </td>
    //                                                                 <td style={tableCellStyle}>
    //                                                                     {stats.total}
    //                                                                 </td>
    //                                                                 <td style={tableCellStyle}>
    //                                                                     {stats.available}
    //                                                                 </td>
    //                                                                 <td
    //                                                                     style={{
    //                                                                         ...tableCellStyle,
    //                                                                         textAlign: "left",
    //                                                                         verticalAlign: "top",
    //                                                                     }}
    //                                                                 >
    //                                                                     {rowItem.isPlaceholder ? (
    //                                                                         <span
    //                                                                             style={{
    //                                                                                 fontSize: "12px",
    //                                                                                 color: "#777",
    //                                                                             }}
    //                                                                         >
    //                                                                             No tables configured.
    //                                                                         </span>
    //                                                                     ) : rowItem.configurations.length > 0 &&
    //                                                                         stats.total > 0 ? (
    //                                                                         <div style={tableVisualContainerStyle}>
    //                                                                             {rowItem.configurations.flatMap(
    //                                                                                 (config) =>
    //                                                                                     config.individualTables
    //                                                                                         .filter(
    //                                                                                             (table) =>
    //                                                                                                 table.capacityValue ===
    //                                                                                                 rowItem.capacity
    //                                                                                         )
    //                                                                                         .map((table) => {
    //                                                                                             const displayStatus = getComputedTableStatus(
    //                                                                                                 table,
    //                                                                                                 globalSelectedDate,
    //                                                                                                 globalSelectedTime,
    //                                                                                                 actualCurrentDate,
    //                                                                                                 actualCurrentTime
    //                                                                                             );



    //                                                                                             const shortId = getShortTableIdentifier(table.code);
    //                                                                                             return (
    //                                                                                                 <div
    //                                                                                                     key={table.id}
    //                                                                                                     style={
    //                                                                                                         tableRepresentationWrapperStyleNoHighlight
    //                                                                                                     }
    //                                                                                                     title={`Table ${shortId} (Cap: ${table.capacityValue}) - Status: ${getDisplayStatus(table.seatingStatus)}. Click to manage.`}

    //                                                                                                 >
    //                                                                                                     <div
    //                                                                                                         style={{
    //                                                                                                             ...individualTableStyle,
    //                                                                                                             backgroundColor:
    //                                                                                                                 statusColors[displayStatus] || statusColors["Maintenance"],
    //                                                                                                         }}
    //                                                                                                         onClick={() =>
    //                                                                                                             handleIndividualTableClick(
    //                                                                                                                 rowItem.floor.id,
    //                                                                                                                 config.id,
    //                                                                                                                 table
    //                                                                                                             )
    //                                                                                                         }
    //                                                                                                     >
    //                                                                                                         {shortId}
    //                                                                                                     </div>
    //                                                                                                 </div>
    //                                                                                             );
    //                                                                                         })
    //                                                                             )}
    //                                                                         </div>
    //                                                                     ) : (
    //                                                                         <span
    //                                                                             style={{
    //                                                                                 fontSize: "12px",
    //                                                                                 color: "#777",
    //                                                                             }}
    //                                                                         >
    //                                                                             No tables configured for capacity{" "}
    //                                                                             {rowItem.capacity}.
    //                                                                         </span>
    //                                                                     )}
    //                                                                 </td>
    //                                                                 <td style={tableCellStyle}>
    //                                                                     <button
    //                                                                         style={actionButtonStyle}
    //                                                                         onClick={() =>
    //                                                                             handleEditFloorSetup(
    //                                                                                 rowItem.floor.id,
    //                                                                                 rowItem.isPlaceholder
    //                                                                                     ? null
    //                                                                                     : rowItem.capacity
    //                                                                             )
    //                                                                         }
    //                                                                         title={`Edit Floor or Table Configurations`}
    //                                                                     >
    //                                                                         <FaEdit />
    //                                                                     </button>
    //                                                                 </td>
    //                                                                 <td style={{
    //                                                                     ...tableCellStyle,
    //                                                                     fontSize: '12px',
    //                                                                     wordBreak: 'break-word',
    //                                                                     maxWidth: '200px', // ✅ Add max width
    //                                                                     minWidth: "150px",
    //                                                                     whiteSpace: 'normal', // ✅ Ensure text wraps
    //                                                                     overflow: 'hidden',
    //                                                                     textOverflow: 'ellipsis'
    //                                                                 }}>
    //                                                                     {
    //                                                                         rowItem.configurations
    //                                                                             .flatMap(config => config.individualTables.map(table => table.id))
    //                                                                             .join(', ')
    //                                                                     }
    //                                                                 </td>
    //                                                             </tr>
    //                                                         );
    //                                                     })}
    //                                                 </tbody>
    //                                             </table>
    //                                         </div>
    //                                     </>
    //                                 )}
    //                             </div>
    //                         </div>
    //                     </div>
    //                 </div>

    //                 <div
    //                     style={bookingsViewSectionStyle}
    //                     className="bookings-sidebar-scroll-area col-lg-4 col-md-4 col-sm-12 col-12"
    //                 >
    //                     <AllBookingsView
    //                         tableHeaderStyle={tableHeaderStyle}
    //                         tableCellStyle={tableCellStyle}
    //                         evenRowStyle={evenRowStyle}
    //                         oddRowStyle={oddRowStyle}
    //                         paginationButtonStyle={paginationButtonStyle}
    //                     />
    //                     <WaitlistView
    //                         waitlist={waitlist}
    //                         onSeatGuestRequest={handleSeatGuestRequest}
    //                         onRemoveFromWaitlist={handleRemoveFromWaitlist}
    //                         tableHeaderStyle={tableHeaderStyle}
    //                         tableCellStyle={tableCellStyle}
    //                         evenRowStyle={evenRowStyle}
    //                         oddRowStyle={oddRowStyle}
    //                         paginationButtonStyle={paginationButtonStyle}
    //                         smallActionButtonStyle={smallActionButtonStyle}
    //                         statusColors={statusColors}
    //                         waitlistStatusColors={waitlistStatusColors}
    //                     />
    //                 </div>

    //                 {/* --- ALL MODALS --- */}
    //                 {showFloorEditModal && editingFloor && (
    //                     <div style={{ ...modalStyle, maxWidth: "600px" }}>
    //                         <div style={modalHeaderStyle}>
    //                             <h3 style={{ margin: 0, fontSize: "18px" }}>
    //                                 {editingFloor.isAdded === false
    //                                     ? "Add New Floor"
    //                                     : focusedCapacity
    //                                         ? `Edit Tables for Capacity ${focusedCapacity} on ${editingFloor.floorName}`
    //                                         : `Edit Floor: ${editingFloor.floorName}`}
    //                             </h3>
    //                             <button
    //                                 style={modalCloseButtonStyle}
    //                                 onClick={() => {
    //                                     setShowFloorEditModal(false);
    //                                     setEditingFloor(null);
    //                                     setFocusedCapacity(null);
    //                                 }}
    //                                 title="Close"
    //                             >
    //                                 <FaTimes />
    //                             </button>
    //                         </div>
    //                         {!focusedCapacity && (
    //                             <>
    //                                 <div style={inputContainerStyle}>
    //                                     <label htmlFor="editFloorName" style={labelStyle}>
    //                                         Floor Name:<span style={{ color: "red" }}>*</span>
    //                                     </label>
    //                                     <input
    //                                         id="editFloorName"
    //                                         type="text"
    //                                         value={editingFloor.floorName}
    //                                         onChange={(e) =>
    //                                             setEditingFloor({
    //                                                 ...editingFloor,
    //                                                 floorName: e.target.value,
    //                                             })
    //                                         }
    //                                         style={inputFieldStyle}
    //                                     />
    //                                 </div>
    //                                 <div style={inputContainerStyle}>
    //                                     <label htmlFor="floorCleaningDuration" style={labelStyle}>
    //                                         Default Cleaning Duration (min):
    //                                         <span style={{ color: "red" }}>*</span>
    //                                     </label>
    //                                     <input
    //                                         id="floorCleaningDuration"
    //                                         type="number"
    //                                         min="0"
    //                                         value={
    //                                             editingFloor.defaultCleaningDuration === undefined
    //                                                 ? ""
    //                                                 : String(editingFloor.defaultCleaningDuration)
    //                                         }
    //                                         onChange={(e) => {
    //                                             const val = e.target.value;
    //                                             setEditingFloor({
    //                                                 ...editingFloor,
    //                                                 defaultCleaningDuration:
    //                                                     val === ""
    //                                                         ? undefined
    //                                                         : parseInt(val, 10) >= 0
    //                                                             ? parseInt(val, 10)
    //                                                             : 0,
    //                                             });
    //                                         }}
    //                                         style={inputFieldStyle}
    //                                         placeholder={`e.g., 15`}
    //                                     />
    //                                 </div>
    //                             </>
    //                         )}
    //                         <div
    //                             style={{
    //                                 marginTop: "20px",
    //                                 borderTop: "1px solid #eee",
    //                                 paddingTop: "15px",
    //                             }}
    //                         >
    //                             <h4 style={{ margin: "0 0 10px 0", fontSize: "16px" }}>
    //                                 {focusedCapacity
    //                                     ? `Manage Tables for Capacity: ${focusedCapacity}`
    //                                     : "Table Configurations (by Capacity):"}
    //                             </h4>
    //                             {editingFloor.configurations.length === 0 && !focusedCapacity && (
    //                                 <p
    //                                     style={{
    //                                         fontSize: "13px",
    //                                         color: "#555",
    //                                         textAlign: "center",
    //                                     }}
    //                                 >
    //                                     No table configurations defined. Add one below.
    //                                 </p>
    //                             )}
    //                             {(focusedCapacity
    //                                 ? editingFloor.configurations.filter(
    //                                     (c) => c.capacity === focusedCapacity
    //                                 )
    //                                 : editingFloor.configurations
    //                             )
    //                                 .sort((a, b) => parseInt(a.capacity) - parseInt(b.capacity))
    //                                 .map((config) => {
    //                                     if (focusedCapacity && config.capacity !== focusedCapacity) {
    //                                         return null;
    //                                     }
    //                                     return (
    //                                         <div key={config.id} style={configSectionStyle}>
    //                                             <div style={configHeaderStyle}>
    //                                                 <span>
    //                                                     Capacity: <strong>{config.capacity}</strong> (
    //                                                     {config.individualTables.length} Table
    //                                                     {config.individualTables.length === 1 ? "" : "s"})
    //                                                 </span>
    //                                             </div>
    //                                             {config.individualTables.length > 0 ? (
    //                                                 <div style={individualTableListStyleInModal}>
    //                                                     {config.individualTables.map((table) => (
    //                                                         <div
    //                                                             key={table.id}
    //                                                             style={individualTableEditRowStyle}
    //                                                         >
    //                                                             <span style={{ minWidth: "70px" }}>
    //                                                                 {getShortTableIdentifier(table.id)}
    //                                                             </span>
    //                                                             <span style={{
    //                                                                 marginLeft: '10px',
    //                                                                 marginRight: 'auto',
    //                                                                 padding: '4px 8px',
    //                                                                 backgroundColor: '#f0f0f0',
    //                                                                 borderRadius: '4px',
    //                                                                 fontSize: '12px',
    //                                                                 color: '#333'
    //                                                             }}>
    //                                                                 Status: <strong>{getDisplayStatus(table.seatingStatus)}</strong>
    //                                                             </span>
    //                                                             <button
    //                                                                 onClick={() =>
    //                                                                     handleRemoveIndividualTableFromEditingFloorConfig(
    //                                                                         config.id,
    //                                                                         table.id
    //                                                                     )
    //                                                                 }
    //                                                                 style={{
    //                                                                     ...actionButtonStyle,
    //                                                                     fontSize: "13px",
    //                                                                     color: "#c82333",
    //                                                                     padding: "3px 5px",
    //                                                                 }}
    //                                                                 title="Delete this Table"
    //                                                             >
    //                                                                 <FaTrash />
    //                                                             </button>
    //                                                         </div>
    //                                                     ))}
    //                                                 </div>
    //                                             ) : (
    //                                                 <p
    //                                                     style={{
    //                                                         fontSize: "12px",
    //                                                         color: "#666",
    //                                                         paddingLeft: "10px",
    //                                                         margin: "5px 0",
    //                                                     }}
    //                                                 >
    //                                                     No tables in this configuration.
    //                                                 </p>
    //                                             )}
    //                                         </div>
    //                                     );
    //                                 })}
    //                             {focusedCapacity &&
    //                                 !editingFloor.configurations.some(
    //                                     (c) => c.capacity === focusedCapacity
    //                                 ) && (
    //                                     <p
    //                                         style={{
    //                                             fontSize: "13px",
    //                                             color: "#777",
    //                                             textAlign: "center",
    //                                             marginTop: "10px",
    //                                         }}
    //                                     >
    //                                         Configuration for capacity "{focusedCapacity}" does not
    //                                         exist. Add it via general floor edit.
    //                                     </p>
    //                                 )}
    //                             {!focusedCapacity && (
    //                                 <div
    //                                     style={{
    //                                         marginTop: "20px",
    //                                         borderTop: "1px dashed #ccc",
    //                                         paddingTop: "15px",
    //                                     }}
    //                                 >
    //                                     <h5 style={{ margin: "0 0 10px 0", fontSize: "15px" }}>
    //                                         Add New Configuration Group:
    //                                     </h5>
    //                                     <div style={inputRowStyle}>
    //                                         <div style={inputContainerHalfStyle}>
    //                                             <label htmlFor="newConfigCapacity" style={labelStyle}>
    //                                                 Capacity:<span style={{ color: "red" }}>*</span>
    //                                             </label>
    //                                             <input
    //                                                 id="newConfigCapacity"
    //                                                 type="number"
    //                                                 min="1"
    //                                                 value={newConfigCapacity}
    //                                                 onChange={(e) => setNewConfigCapacity(e.target.value)}
    //                                                 style={inputFieldStyle}
    //                                                 placeholder="e.g., 2, 4"
    //                                             />
    //                                         </div>
    //                                         <div style={inputContainerHalfStyle}>
    //                                             <label htmlFor="newConfigNumTables" style={labelStyle}>
    //                                                 # Tables for this Capacity:
    //                                                 <span style={{ color: "red" }}>*</span>
    //                                             </label>
    //                                             <input
    //                                                 id="newConfigNumTables"
    //                                                 type="number"
    //                                                 min="1"
    //                                                 value={newConfigNumTables}
    //                                                 onChange={(e) => setNewConfigNumTables(e.target.value)}
    //                                                 style={inputFieldStyle}
    //                                                 placeholder="e.g., 5"
    //                                             />
    //                                         </div>
    //                                     </div>
    //                                     <button
    //                                         onClick={handleAddConfigurationToEditingFloor}
    //                                         style={{
    //                                             ...buttonStyle,
    //                                             height: "32px",
    //                                             fontSize: "13px",
    //                                             backgroundColor: "#17a2b8",
    //                                             color: "white",
    //                                             padding: "0 10px",
    //                                         }}
    //                                         title="Add Configuration Group"
    //                                         disabled={isPosting}
    //                                     >
    //                                         {isPosting ? (
    //                                             "Adding..."
    //                                         ) : (
    //                                             <>
    //                                                 <FaLayerGroup style={{ marginRight: "5px" }} /> Add
    //                                                 Config Group
    //                                             </>
    //                                         )}
    //                                     </button>
    //                                 </div>
    //                             )}
    //                         </div>
    //                         <div style={buttonContainerStyle}>
    //                         </div>
    //                     </div>
    //                 )}
    //                 {showBookTableModal && bookingTableInfo && (
    //                     <div style={{ ...modalStyle, maxWidth: "450px" }}>
    //                         <div style={modalHeaderStyle}>
    //                             <h3 style={{ margin: 0, fontSize: "18px" }}>
    //                                 Book Table (Cap: {bookingTableInfo.capacity})
    //                             </h3>
    //                             <button
    //                                 style={modalCloseButtonStyle}
    //                                 onClick={() => {
    //                                     setShowBookTableModal(false);
    //                                     setBookingTableInfo(null);
    //                                 }}
    //                                 title="Close"
    //                             >
    //                                 <FaTimes />
    //                             </button>
    //                         </div>
    //                         <p style={{ marginBottom: "15px", fontSize: "14px" }}>
    //                             Table ID:{" "}
    //                             <strong>
    //                                 {getShortTableIdentifier(bookingTableInfo.tableCode)}
    //                             </strong>{" "}
    //                             (Floor:{" "}
    //                             {data.find((f) => f.id === bookingTableInfo.floorId)?.floorName})
    //                         </p>
    //                         <div style={inputRowStyle}>
    //                             <div style={inputContainerHalfStyle}>
    //                                 <label htmlFor="bookingCustomerName" style={labelStyle}>
    //                                     Name:<span style={{ color: "red" }}>*</span>
    //                                 </label>
    //                                 <input
    //                                     id="bookingCustomerName"
    //                                     type="text"
    //                                     value={bookingCustomerName}
    //                                     onChange={(e) => setBookingCustomerName(e.target.value)}
    //                                     style={inputFieldStyle}
    //                                 />
    //                             </div>
    //                             <div style={inputContainerHalfStyle}>
    //                                 <label htmlFor="bookingPhoneNumber" style={labelStyle}>
    //                                     Phone:
    //                                 </label>
    //                                 <input
    //                                     id="bookingPhoneNumber"
    //                                     type="tel"
    //                                     value={bookingPhoneNumber}
    //                                     onChange={(e) => setBookingPhoneNumber(e.target.value)}
    //                                     style={inputFieldStyle}
    //                                 />
    //                             </div>
    //                         </div>
    //                         <div style={inputRowStyle}>
    //                             <div style={inputContainerHalfStyle}>
    //                                 <label htmlFor="bookingDate" style={labelStyle}>
    //                                     Date:<span style={{ color: "red" }}>*</span>
    //                                 </label>
    //                                 <input
    //                                     id="bookingDate"
    //                                     type="date"
    //                                     value={bookingDate}
    //                                     onChange={(e) => setBookingDate(e.target.value)}
    //                                     style={inputFieldStyle}
    //                                     min={getTodayDate()}
    //                                 />
    //                             </div>
    //                             <div style={inputContainerHalfStyle}></div>
    //                         </div>
    //                         <div style={inputRowStyle}>
    //                             <div style={inputContainerHalfStyle}>
    //                                 <label htmlFor="bookingStartTime" style={labelStyle}>
    //                                     Start Time (HH:MM):<span style={{ color: "red" }}>*</span>
    //                                 </label>
    //                                 <input
    //                                     id="bookingStartTime"
    //                                     type="time"
    //                                     value={bookingStartTime}
    //                                     onChange={(e) => setBookingStartTime(e.target.value)}
    //                                     style={inputFieldStyle}
    //                                 />
    //                             </div>
    //                             <div style={inputContainerHalfStyle}>
    //                                 <label htmlFor="bookingDurationMinutes" style={labelStyle}>
    //                                     Duration (minutes):<span style={{ color: "red" }}>*</span>
    //                                 </label>
    //                                 <input
    //                                     id="bookingDurationMinutes"
    //                                     type="number"
    //                                     min="15"
    //                                     step="15"
    //                                     value={bookingDurationMinutes}
    //                                     onChange={(e) => setBookingDurationMinutes(e.target.value)}
    //                                     style={inputFieldStyle}
    //                                     placeholder={`e.g., 90`}
    //                                 />
    //                             </div>
    //                         </div>
    //                         <p
    //                             style={{
    //                                 fontSize: "11px",
    //                                 color: "#555",
    //                                 marginTop: "-8px",
    //                                 marginBottom: "10px",
    //                             }}
    //                         >
    //                             Start Time and a positive Duration are required.
    //                         </p>
    //                         <p
    //                             style={{
    //                                 fontSize: "11px",
    //                                 color: "#333",
    //                                 marginTop: "-8px",
    //                                 marginBottom: "10px",
    //                             }}
    //                         >
    //                             Restaurant Hours: {RESTAURANT_OPEN_TIME_STR} -{" "}
    //                             {RESTAURANT_CLOSE_TIME_STR}{" "}
    //                             {timeToMinutes(RESTAURANT_CLOSE_TIME_STR) <
    //                                 timeToMinutes(RESTAURANT_OPEN_TIME_STR)
    //                                 ? "(next day)"
    //                                 : "(same day)"}
    //                             .
    //                         </p>
    //                         <div style={inputContainerStyle}>
    //                             <label htmlFor="bookingNotes" style={labelStyle}>
    //                                 Notes:
    //                             </label>
    //                             <textarea
    //                                 id="bookingNotes"
    //                                 value={bookingNotes}
    //                                 onChange={(e) => setBookingNotes(e.target.value)}
    //                                 style={{
    //                                     ...inputFieldStyle,
    //                                     height: "60px",
    //                                     resize: "vertical",
    //                                 }}
    //                             />
    //                         </div>
    //                         <div style={buttonContainerStyle}>
    //                             <button
    //                                 onClick={handleConfirmBooking}
    //                                 style={{
    //                                     ...buttonStyle,
    //                                     width: "180px",
    //                                     backgroundColor: statusColors.Reserved,
    //                                 }}
    //                             >
    //                                 <FaBookmark style={{ marginRight: "8px" }} /> Confirm Booking
    //                             </button>
    //                         </div>
    //                     </div>
    //                 )}


    //                 {showIndividualTableModal && editingTableInfo && (
    //                     <div style={{ ...modalStyle, maxWidth: "450px" }}>
    //                         <div style={modalHeaderStyle}>
    //                             <h3 style={{ margin: 0, fontSize: "18px" }}>
    //                                 Manage Table (Cap: {editingTableInfo.capacity})
    //                             </h3>
    //                             <button
    //                                 style={modalCloseButtonStyle}
    //                                 onClick={() => {
    //                                     setShowIndividualTableModal(false);
    //                                     setEditingTableInfo(null);
    //                                 }}
    //                                 title="Close"
    //                             >
    //                                 <FaTimes />
    //                             </button>
    //                         </div>
    //                         <p style={{ marginBottom: "15px", fontSize: "14px" }}>
    //                             Table ID: <strong>{getShortTableIdentifier(editingTableInfo.tableId)}</strong>
    //                         </p>
    //                         <div style={detailsBoxStyle}>
    //                             <strong>Current Booking Details:</strong>
    //                             {(() => {
    //                                 const floorObj = data.find(f => f.id === editingTableInfo.floorId);
    //                                 const configObj = floorObj?.configurations.find(c => c.id === editingTableInfo.configId);
    //                                 const tableObj = configObj?.individualTables.find(t => t.id === editingTableInfo.tableId);
    //                                 if (!tableObj || !Array.isArray(tableObj.bookings) || tableObj.bookings.length === 0) {
    //                                     return <div style={{ fontSize: "13px", color: "#777" }}>No bookings for this table.</div>;
    //                                 }
    //                                 const nowMinutes = timeToMinutes(actualCurrentTime);
    //                                 const today = actualCurrentDate;
    //                                 let currentBooking = null;
    //                                 let nextBooking = null;
    //                                 const todayBookings = tableObj.bookings
    //                                     .filter(b => b.date === today)
    //                                     .sort((a, b) => (timeToMinutes(a.time) ?? 0) - (timeToMinutes(b.time) ?? 0));
    //                                 for (const b of todayBookings) {
    //                                     const start = timeToMinutes(b.time);
    //                                     const end = timeToMinutes(b.endTime);
    //                                     if (start !== null && end !== null) {
    //                                         if (nowMinutes >= start && nowMinutes < end) {
    //                                             currentBooking = b;
    //                                             break;
    //                                         }
    //                                         if (nowMinutes < start && !nextBooking) {
    //                                             nextBooking = b;
    //                                         }
    //                                     }
    //                                 }
    //                                 const showBooking = currentBooking || nextBooking || todayBookings[0];
    //                                 if (!showBooking) {
    //                                     return <div style={{ fontSize: "13px", color: "#777" }}>No bookings for this table.</div>;
    //                                 }
    //                                 return (
    //                                     <div style={{ fontSize: "13px", color: "#333" }}>
    //                                         <div>
    //                                             <strong>Booking ID:</strong> {showBooking.id ?? "N/A"}
    //                                         </div>
    //                                         <div>
    //                                             <strong>Status:</strong> {editingTableInfo.currentStatus || "N/A"}
    //                                         </div>
    //                                         <div>
    //                                             <strong>Guest Name:</strong> {showBooking.name || showBooking.guestName || "N/A"}
    //                                         </div>
    //                                         <div>
    //                                             <strong>Phone:</strong> {showBooking.phone || showBooking.phoneNumber || "N/A"}
    //                                         </div>
    //                                         <div>
    //                                             <strong>Date:</strong> {showBooking.date || showBooking.bookingDate || "N/A"}
    //                                         </div>
    //                                         <div>
    //                                             <strong>Time:</strong> {showBooking.time || "N/A"} - {showBooking.endTime || "N/A"}
    //                                         </div>
    //                                     </div>
    //                                 );
    //                             })()}
    //                         </div>

    //                         <div style={inputContainerStyle}>
    //                             <label htmlFor="editTableStatus" style={labelStyle}>
    //                                 Set New Table Status:
    //                             </label>
    //                             <select
    //                                 id="editTableStatus"
    //                                 name="newStatus"
    //                                 value={editingTableInfo.newStatus}
    //                                 onChange={handleIndividualTableStatusChange}
    //                                 style={inputFieldStyle}
    //                             >
    //                                 {tableStatuses.map((status) => (
    //                                     <option key={status} value={status}>
    //                                         {status}
    //                                     </option>
    //                                 ))}
    //                             </select>
    //                         </div>

    //                         <div style={buttonContainerStyle}>
    //                             <button
    //                                 onClick={submitIndividualTableStatus}
    //                                 style={{ ...buttonStyle, width: "180px" }}
    //                             >
    //                                 <FaSave style={{ marginRight: "8px" }} />
    //                                 Update Status
    //                             </button>
    //                         </div>
    //                     </div>
    //                 )}

    //                 {showAddToWaitlistModal && (
    //                     <div
    //                         style={{
    //                             position: "fixed",
    //                             top: "50%",
    //                             left: "50%",
    //                             transform: "translate(-50%, -50%)",
    //                             backgroundColor: "#fff",
    //                             padding: "24px",
    //                             borderRadius: "8px",
    //                             boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
    //                             zIndex: 9999,
    //                             width: "100%",
    //                             maxWidth: "500px",
    //                         }}
    //                     >
    //                         <div
    //                             style={{
    //                                 display: "flex",
    //                                 justifyContent: "space-between",
    //                                 alignItems: "center",
    //                                 marginBottom: "16px",
    //                             }}
    //                         >
    //                             <h3 style={{ margin: 0, fontSize: "18px" }}>
    //                                 Add Guest to Waitlist
    //                             </h3>
    //                             <button
    //                                 onClick={() => setShowAddToWaitlistModal(false)}
    //                                 title="Close"
    //                                 style={{
    //                                     background: "transparent",
    //                                     border: "none",
    //                                     fontSize: "18px",
    //                                     cursor: "pointer",
    //                                 }}
    //                             >
    //                                 <FaTimes />
    //                             </button>
    //                         </div>

    //                         <div style={{ marginBottom: "16px" }}>
    //                             <label
    //                                 htmlFor="filterFloorWaitlist"
    //                                 style={{
    //                                     fontWeight: "bold",
    //                                     marginBottom: "4px",
    //                                     display: "block",
    //                                 }}
    //                             >
    //                                 <FaLayerGroup style={{ marginRight: "4px" }} />
    //                                 Floor: <span style={{ color: "red" }}>*</span>
    //                             </label>
    //                             <select
    //                                 id="filterFloorWaitlist"
    //                                 value={waitlistModalFloorId}
    //                                 onChange={(e) => setWaitlistModalFloorId(e.target.value)}
    //                                 style={{
    //                                     width: "100%",
    //                                     padding: "8px",
    //                                     border: "1px solid #ccc",
    //                                     borderRadius: "4px",
    //                                     fontSize: "14px",
    //                                 }}
    //                             >
    //                                 <option value="">Select Floor</option>
    //                                 {data
    //                                     .sort((a, b) => a.floorName.localeCompare(b.floorName))
    //                                     .map((floor) => (
    //                                         <option key={floor.id} value={floor.id}>
    //                                             {floor.floorName}
    //                                         </option>
    //                                     ))}
    //                             </select>
    //                         </div>

    //                         <div style={{ marginBottom: "16px" }}>
    //                             <label
    //                                 htmlFor="waitlistGuestName"
    //                                 style={{
    //                                     fontWeight: "bold",
    //                                     display: "block",
    //                                     marginBottom: "4px",
    //                                 }}
    //                             >
    //                                 Guest Name: <span style={{ color: "red" }}>*</span>
    //                             </label>
    //                             <input
    //                                 id="waitlistGuestName"
    //                                 type="text"
    //                                 value={newWaitlistEntryName}
    //                                 onChange={(e) => setNewWaitlistEntryName(e.target.value)}
    //                                 style={{
    //                                     width: "100%",
    //                                     padding: "8px",
    //                                     border: "1px solid #ccc",
    //                                     borderRadius: "4px",
    //                                     fontSize: "14px",
    //                                 }}
    //                             />
    //                         </div>

    //                         <div
    //                             style={{
    //                                 display: "flex",
    //                                 flexWrap: "wrap",
    //                                 gap: "16px",
    //                                 marginBottom: "16px",
    //                             }}
    //                         >
    //                             <div style={{ flex: "1 1 45%" }}>
    //                                 <label
    //                                     htmlFor="waitlistPartySize"
    //                                     style={{
    //                                         fontWeight: "bold",
    //                                         display: "block",
    //                                         marginBottom: "4px",
    //                                     }}
    //                                 >
    //                                     Party Size: <span style={{ color: "red" }}>*</span>
    //                                 </label>
    //                                 <input
    //                                     id="waitlistPartySize"
    //                                     type="number"
    //                                     min="1"
    //                                     value={newWaitlistEntryPartySize}
    //                                     onChange={(e) => setNewWaitlistEntryPartySize(e.target.value)}
    //                                     style={{
    //                                         width: "100%",
    //                                         padding: "8px",
    //                                         border: "1px solid #ccc",
    //                                         borderRadius: "4px",
    //                                         fontSize: "14px",
    //                                     }}
    //                                 />
    //                             </div>

    //                             <div style={{ flex: "1 1 45%" }}>
    //                                 <label
    //                                     htmlFor="waitlistGuestPhone"
    //                                     style={{
    //                                         fontWeight: "bold",
    //                                         display: "block",
    //                                         marginBottom: "4px",
    //                                     }}
    //                                 >
    //                                     Phone (Optional):
    //                                 </label>
    //                                 <input
    //                                     id="waitlistGuestPhone"
    //                                     type="tel"
    //                                     value={newWaitlistEntryPhone}
    //                                     onChange={(e) => setNewWaitlistEntryPhone(e.target.value)}
    //                                     style={{
    //                                         width: "100%",
    //                                         padding: "8px",
    //                                         border: "1px solid #ccc",
    //                                         borderRadius: "4px",
    //                                         fontSize: "14px",
    //                                     }}
    //                                 />
    //                             </div>

    //                             <div style={{ flex: "1 1 45%" }}>
    //                                 <label
    //                                     htmlFor="bookingDate"
    //                                     style={{
    //                                         fontWeight: "bold",
    //                                         display: "block",
    //                                         marginBottom: "4px",
    //                                     }}
    //                                 >
    //                                     Date: <span style={{ color: "red" }}>*</span>
    //                                 </label>
    //                                 <input
    //                                     id="bookingDate"
    //                                     type="date"
    //                                     value={bookingDate}
    //                                     onChange={(e) => setBookingDate(e.target.value)}
    //                                     min={getTodayDate()}
    //                                     style={{
    //                                         width: "100%",
    //                                         padding: "8px",
    //                                         border: "1px solid #ccc",
    //                                         borderRadius: "4px",
    //                                         fontSize: "14px",
    //                                     }}
    //                                 />
    //                             </div>

    //                             <div style={{ flex: "1 1 45%" }}>
    //                                 <label
    //                                     htmlFor="bookingDurationMinutes"
    //                                     style={{
    //                                         fontWeight: "bold",
    //                                         display: "block",
    //                                         marginBottom: "4px",
    //                                     }}
    //                                 >
    //                                     Duration (minutes): <span style={{ color: "red" }}>*</span>
    //                                 </label>
    //                                 <input
    //                                     id="bookingDurationMinutes"
    //                                     type="number"
    //                                     min="15"
    //                                     step="15"
    //                                     value={bookingDurationMinutes}
    //                                     onChange={(e) => setBookingDurationMinutes(e.target.value)}
    //                                     placeholder={`e.g., 90`}
    //                                     style={{
    //                                         width: "100%",
    //                                         padding: "8px",
    //                                         border: "1px solid #ccc",
    //                                         borderRadius: "4px",
    //                                         fontSize: "14px",
    //                                     }}
    //                                 />
    //                             </div>
    //                         </div>

    //                         <div style={{ display: "flex", justifyContent: "center" }}>
    //                             <button
    //                                 onClick={handleSaveWaitlistEntry}
    //                                 style={{
    //                                     backgroundColor: "#17a2b8",
    //                                     color: "#fff",
    //                                     padding: "10px 16px",
    //                                     border: "none",
    //                                     borderRadius: "4px",
    //                                     cursor: "pointer",
    //                                     fontSize: "14px",
    //                                     width: "180px",
    //                                 }}
    //                             >
    //                                 <FaUserPlus style={{ marginRight: "8px" }} />
    //                                 Add to Waitlist
    //                             </button>
    //                         </div>
    //                     </div>
    //                 )}

    //                 {showSeatWaitlistGuestModal && seatingWaitlistGuestInfo && (
    //                     <div style={{ ...modalStyle, maxWidth: "500px" }}>
    //                         <div style={modalHeaderStyle}>
    //                             <h3 style={{ margin: 0, fontSize: "18px" }}>
    //                                 Seat Waitlisted Guest
    //                             </h3>
    //                             <button
    //                                 style={modalCloseButtonStyle}
    //                                 onClick={() => {
    //                                     setShowSeatWaitlistGuestModal(false);
    //                                     setSeatingWaitlistGuestInfo(null);
    //                                 }}
    //                                 title="Close"
    //                             >
    //                                 <FaTimes />
    //                             </button>
    //                         </div>
    //                         <div style={detailsBoxStyle}>
    //                             <p>
    //                                 <strong>Guest:</strong> {seatingWaitlistGuestInfo.entry.guestName}
    //                             </p>
    //                             <p>
    //                                 <strong>Party Size:</strong>{" "}
    //                                 {seatingWaitlistGuestInfo.entry.capacity}
    //                             </p>
    //                             <p>
    //                                 <strong>Phone:</strong>{" "}
    //                                 {seatingWaitlistGuestInfo.entry.phoneNumber || "N/A"}
    //                             </p>
    //                         </div>

    //                         <div style={inputContainerStyle}>
    //                             <label htmlFor="selectTableForWaitlist" style={labelStyle}>
    //                                 Select Available Table (Capacity {">="}{" "}
    //                                 {seatingWaitlistGuestInfo.entry.capacity}):
    //                             </label>
    //                             {availableTablesForWaitlistGuest.length > 0 ? (
    //                                 <select
    //                                     id="selectTableForWaitlist"
    //                                     value={selectedTableForWaitlistSeating}
    //                                     onChange={(e) =>
    //                                         setSelectedTableForWaitlistSeating(e.target.value)
    //                                     }
    //                                     style={inputFieldStyle}
    //                                 >
    //                                     <option value="">-- Select a Table --</option>
    //                                     {availableTablesForWaitlistGuest.map((table) => (
    //                                         <option key={table.id} value={table.id}>
    //                                             {table.shortId} (Cap: {table.capacityValue}, Floor:{" "}
    //                                             {table.floorName})
    //                                         </option>
    //                                     ))}
    //                                 </select>
    //                             ) : (
    //                                 <p
    //                                     style={{
    //                                         color: statusColors.Occupied,
    //                                         fontSize: "13px",
    //                                         marginTop: "5px",
    //                                     }}
    //                                 >
    //                                     No suitable tables are currently available based on party
    //                                     size.
    //                                 </p>
    //                             )}
    //                         </div>
    //                         <p
    //                             style={{
    //                                 fontSize: "11px",
    //                                 color: "#333",
    //                                 marginTop: "-8px",
    //                                 marginBottom: "10px",
    //                             }}
    //                         >
    //                             Seating at: {actualCurrentDate} {actualCurrentTime} for{" "}
    //                             {90} mins (Restaurant Hours:{" "}
    //                             {RESTAURANT_OPEN_TIME_STR} - {RESTAURANT_CLOSE_TIME_STR}
    //                             ).
    //                         </p>

    //                         <div style={buttonContainerStyle}>
    //                             <button
    //                                 onClick={handleConfirmSeatWaitlistGuest}
    //                                 style={{
    //                                     ...buttonStyle,
    //                                     width: "180px",
    //                                     backgroundColor: statusColors.Available,
    //                                 }}
    //                                 disabled={
    //                                     !selectedTableForWaitlistSeating ||
    //                                     availableTablesForWaitlistGuest.length === 0
    //                                 }
    //                             >
    //                                 <FaBookmark style={{ marginRight: "8px" }} /> Seat Guest
    //                             </button>
    //                         </div>
    //                     </div>
    //                 )}

    //                 {(showFloorEditModal ||
    //                     showBookTableModal ||
    //                     showIndividualTableModal ||
    //                     showAddToWaitlistModal ||
    //                     showSeatWaitlistGuestModal) && (
    //                         <div
    //                             style={{
    //                                 position: "fixed",
    //                                 top: 0,
    //                                 left: 0,
    //                                 right: 0,
    //                                 bottom: 0,
    //                                 backgroundColor: "rgba(0,0,0,0.5)",
    //                                 zIndex: 999,
    //                             }}
    //                         />
    //                     )}
    //             </div>
    //         </div>
    //     );



    return (
        <div className="container-fluid">
            <style>
                {`
            @media screen and (max-width: 768px) {
                .rows {
                    margin-top: 0px !important;
                }
                .main-content-row {
                    flex-direction: column;
                }
                .two-column-container {
                    flex-direction: column !important;
                }
                .column-half {
                    width: 100% !important;
                }
            }
        `}
            </style>

            {/* TOP SECTION: GLOBAL CONTROLS */}
            <div className="row rows">
                <div className="col-12">
                    <div style={globalControlsContainerStyle} className="obr">
                        {/* Left side: Filters */}
                        <div style={{ display: "flex", gap: "15px", alignItems: "center", flex: 1, flexWrap: "wrap" }}>
                            <div style={globalControlItemStyle}>
                                <label htmlFor="globalDate" style={labelStyle}>
                                    <FaCalendarAlt /> Date:
                                </label>
                                <input
                                    type="date"
                                    id="globalDate"
                                    value={globalSelectedDate}
                                    onChange={(e) => setGlobalSelectedDate(e.target.value)}
                                    style={{
                                        ...inputFieldStyle,
                                        width: "auto",
                                        padding: "6px 8px",
                                    }}
                                />
                            </div>
                            <div style={globalControlItemStyle}>
                                <label htmlFor="globalTime" style={labelStyle}>
                                    <FaClock /> Time:
                                </label>
                                <input
                                    type="time"
                                    id="globalTime"
                                    value={globalSelectedTime}
                                    onChange={(e) =>
                                        setGlobalSelectedTime(e.target.value || "")
                                    }
                                    style={{
                                        ...inputFieldStyle,
                                        width: "auto",
                                        padding: "6px 8px",
                                    }}
                                />
                            </div>
                            <div style={globalControlItemStyle}>
                                <label htmlFor="filterFloor" style={labelStyle}>
                                    <FaLayerGroup style={{ marginRight: "3px" }} /> Floor:
                                </label>
                                <select
                                    id="filterFloor"
                                    value={selectedFilterFloorId}
                                    onChange={(e) => setSelectedFilterFloorId(e.target.value)}
                                    style={{
                                        ...inputFieldStyle,
                                        width: "150px",
                                        padding: "6px 8px",
                                    }}
                                >
                                    <option value="">All Floors</option>
                                    {data
                                        .sort((a, b) => a.floorName.localeCompare(b.floorName))
                                        .map((floor) => (
                                            <option key={floor.id} value={floor.id}>
                                                {floor.floorName}
                                            </option>
                                        ))}
                                </select>
                            </div>
                        </div>

                        {/* Right side: Buttons */}
                        <div style={{ display: "flex", gap: "10px", alignItems: "center", flexDirection: window.innerWidth > 500 && window.innerWidth < 1500 ? "column" : " " }}>
                            <button
                                onClick={handleAddWaitlistGuestClick}
                                style={{
                                    ...buttonStyle,
                                    backgroundColor: "#17a2b8",
                                }}
                                title="Add guest to waitlist"
                            >
                                <FaUserPlus style={{ marginRight: "5px" }} /> Add to Waitlist
                            </button>
                            <button
                                onClick={handleAddFloorRow}
                                style={{
                                    ...buttonStyle,
                                    fontWeight: "bold",
                                }}
                                title="Add a new floor"
                            >
                                <FaPlus style={{ marginRight: "3px" }} />
                                Add Floor
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* MAIN TABLE - FULL WIDTH */}
            <div className="row rows">
                <div className="col-12">
                    <div style={pageContainerStyle}>
                        <div style={mainTableSectionStyle}>
                            <StatusLegend />

                            <div
                                style={{
                                    marginTop: "10px",
                                    flexGrow: 1,
                                    overflowY: "auto",
                                    height: "auto",
                                    maxHeight: "60vh",
                                    scrollbarWidth: "thin",
                                }}
                                className="rep"
                            >
                                {processedDataForDisplay.length === 0 ? (
                                    <p
                                        style={{
                                            textAlign: "center",
                                            marginTop: "20px",
                                            fontSize: "16px",
                                        }}
                                    >
                                        {data.length === 0
                                            ? "No floors available from API. Click 'Add Floor' to create one."
                                            : "No tables match the current filter criteria."}
                                    </p>
                                ) : (
                                    <>
                                        <div style={{}} className="rep2 pe-2">
                                            <table
                                                style={{ width: "100%", borderCollapse: "collapse" }}
                                                className="table"
                                            >
                                                <thead>
                                                    <tr>
                                                        <th style={tableHeaderStyle}>S.No</th>
                                                        <th style={tableHeaderStyle}>Floor Name</th>
                                                        <th style={{ ...tableHeaderStyle, width: "80px" }}>
                                                            Capacity
                                                        </th>
                                                        <th style={{ ...tableHeaderStyle, width: "100px" }}>
                                                            Total Tables
                                                        </th>
                                                        <th style={{ ...tableHeaderStyle, width: "120px" }}>
                                                            Available
                                                        </th>
                                                        <th
                                                            style={{ ...tableHeaderStyle, minWidth: "250px" }}
                                                        >
                                                            Tables Status & Actions
                                                        </th>
                                                        <th style={{ ...tableHeaderStyle, width: "100px" }}>
                                                            Floor Actions
                                                        </th>
                                                        <th style={{ ...tableHeaderStyle, width: "100px" }}>
                                                            TableId
                                                        </th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {processedDataForDisplay.map((rowItem, index) => {
                                                        const stats = getRowTableStats(rowItem);
                                                        return (
                                                            <tr
                                                                key={rowItem.uniqueRowId}
                                                                style={index % 2 === 0 ? evenRowStyle : oddRowStyle}
                                                            >
                                                                <td style={tableCellStyle}>{index + 1}</td>
                                                                <td style={tableCellStyle}>
                                                                    {rowItem.floor.floorName}
                                                                </td>
                                                                <td style={tableCellStyle}>
                                                                    {rowItem.capacity}
                                                                </td>
                                                                <td style={tableCellStyle}>
                                                                    {stats.total}
                                                                </td>
                                                                <td style={tableCellStyle}>
                                                                    {stats.available}
                                                                </td>
                                                                <td
                                                                    style={{
                                                                        ...tableCellStyle,
                                                                        textAlign: "left",
                                                                        verticalAlign: "top",
                                                                    }}
                                                                >
                                                                    {rowItem.isPlaceholder ? (
                                                                        <span
                                                                            style={{
                                                                                fontSize: "12px",
                                                                                color: "#777",
                                                                            }}
                                                                        >
                                                                            No tables configured.
                                                                        </span>
                                                                    ) : rowItem.configurations.length > 0 &&
                                                                        stats.total > 0 ? (
                                                                        <div style={tableVisualContainerStyle}>
                                                                            {rowItem.configurations.flatMap(
                                                                                (config) =>
                                                                                    config.individualTables
                                                                                        .filter(
                                                                                            (table) =>
                                                                                                table.capacityValue ===
                                                                                                rowItem.capacity
                                                                                        )
                                                                                        .map((table) => {
                                                                                            const displayStatus = getComputedTableStatus(
                                                                                                table,
                                                                                                globalSelectedDate,
                                                                                                globalSelectedTime,
                                                                                                actualCurrentDate,
                                                                                                actualCurrentTime
                                                                                            );

                                                                                            const shortId = getShortTableIdentifier(table.code);
                                                                                            return (
                                                                                                <div
                                                                                                    key={table.id}
                                                                                                    style={
                                                                                                        tableRepresentationWrapperStyleNoHighlight
                                                                                                    }
                                                                                                    title={`Table ${shortId} (Cap: ${table.capacityValue}) - Status: ${getDisplayStatus(table.seatingStatus)}. Click to manage.`}
                                                                                                >
                                                                                                    <div
                                                                                                        style={{
                                                                                                            ...individualTableStyle,
                                                                                                            backgroundColor:
                                                                                                                statusColors[displayStatus] || statusColors["Maintenance"],
                                                                                                        }}
                                                                                                        onClick={() =>
                                                                                                            handleIndividualTableClick(
                                                                                                                rowItem.floor.id,
                                                                                                                config.id,
                                                                                                                table
                                                                                                            )
                                                                                                        }
                                                                                                    >
                                                                                                        {shortId}
                                                                                                    </div>
                                                                                                </div>
                                                                                            );
                                                                                        })
                                                                            )}
                                                                        </div>
                                                                    ) : (
                                                                        <span
                                                                            style={{
                                                                                fontSize: "12px",
                                                                                color: "#777",
                                                                            }}
                                                                        >
                                                                            No tables configured for capacity{" "}
                                                                            {rowItem.capacity}.
                                                                        </span>
                                                                    )}
                                                                </td>
                                                                <td style={tableCellStyle}>
                                                                    <button
                                                                        style={actionButtonStyle}
                                                                        onClick={() =>
                                                                            handleEditFloorSetup(
                                                                                rowItem.floor.id,
                                                                                rowItem.isPlaceholder
                                                                                    ? null
                                                                                    : rowItem.capacity
                                                                            )
                                                                        }
                                                                        title={`Edit Floor or Table Configurations`}
                                                                    >
                                                                        <FaEdit />
                                                                    </button>
                                                                </td>
                                                                <td style={{
                                                                    ...tableCellStyle,
                                                                    fontSize: '12px',
                                                                    wordBreak: 'break-word',
                                                                    maxWidth: '200px',
                                                                    minWidth: "150px",
                                                                    whiteSpace: 'normal',
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis'
                                                                }}>
                                                                    {
                                                                        rowItem.configurations
                                                                            .flatMap(config => config.individualTables.map(table => table.id))
                                                                            .join(', ')
                                                                    }
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* TWO COLUMNS BELOW - SIDE BY SIDE */}
            <div className="row rows" style={{ marginTop: "20px" }}>
                <div
                    className="two-column-container"
                    style={{
                        display: "flex",
                        flexDirection: window.innerWidth < 768 ? "column" : "row",
                        gap: "20px",
                        width: "100%"
                    }}
                >
                    {/* LEFT COLUMN - BOOKINGS VIEW */}
                    <div
                        className="column-half"
                        style={{
                            flex: 1,
                            minWidth: 0,
                            width: window.innerWidth < 768 ? "100%" : "50%",
                            display: "flex",
                            flexDirection: "column",
                            height: "500px" /* Fixed height for the entire column */
                        }}
                    >
                        <div style={{
                            ...bookingsViewSectionStyle,
                            display: "flex",
                            flexDirection: "column",
                            height: "100%", /* Take full height of parent */
                            overflow: "hidden" /* Prevent outer container from scrolling */
                        }} className="bookings-sidebar-scroll-area">
                            {/* Create a wrapper that isolates the table scroll */}
                            <div style={{
                                display: "flex",
                                flexDirection: "column",
                                height: "100%",
                                width: "100%"
                            }}>
                                {/* This div will contain the AllBookingsView but control its scroll */}
                                <div style={{
                                    flex: 1,
                                    overflow: "auto",
                                    position: "relative"
                                }}>
                                    <AllBookingsView
                                        tableHeaderStyle={tableHeaderStyle}
                                        tableCellStyle={tableCellStyle}
                                        evenRowStyle={evenRowStyle}
                                        oddRowStyle={oddRowStyle}
                                        paginationButtonStyle={paginationButtonStyle}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* RIGHT COLUMN - WAITLIST VIEW */}
                    <div
                        className="column-half"
                        style={{
                            flex: 1,
                            minWidth: 0,
                            width: window.innerWidth < 768 ? "100%" : "50%",
                            display: "flex",
                            flexDirection: "column",
                            height: "500px" /* Fixed height for the entire column */
                        }}
                    >
                        <div style={{
                            ...bookingsViewSectionStyle,
                            display: "flex",
                            flexDirection: "column",
                            height: "100%", /* Take full height of parent */
                            overflow: "hidden" /* Prevent outer container from scrolling */
                        }} className="bookings-sidebar-scroll-area">
                            {/* Create a wrapper that isolates the table scroll */}
                            <div style={{
                                display: "flex",
                                flexDirection: "column",
                                height: "100%",
                                width: "100%"
                            }}>
                                {/* This div will contain the WaitlistView but control its scroll */}
                                <div style={{
                                    flex: 1,
                                    overflow: "auto",
                                    position: "relative"
                                }}>
                                    <WaitlistView
                                        waitlist={waitlist}
                                        onSeatGuestRequest={handleSeatGuestRequest}
                                        onRemoveFromWaitlist={handleRemoveFromWaitlist}
                                        tableHeaderStyle={tableHeaderStyle}
                                        tableCellStyle={tableCellStyle}
                                        evenRowStyle={evenRowStyle}
                                        oddRowStyle={oddRowStyle}
                                        paginationButtonStyle={paginationButtonStyle}
                                        smallActionButtonStyle={smallActionButtonStyle}
                                        statusColors={statusColors}
                                        waitlistStatusColors={waitlistStatusColors}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* --- ALL MODALS --- */}
            {showFloorEditModal && editingFloor && (
                <div style={{ ...modalStyle, maxWidth: "600px" }}>
                    <div style={modalHeaderStyle}>
                        <h3 style={{ margin: 0, fontSize: "18px" }}>
                            {editingFloor.isAdded === false
                                ? "Add New Floor"
                                : focusedCapacity
                                    ? `Edit Tables for Capacity ${focusedCapacity} on ${editingFloor.floorName}`
                                    : `Edit Floor: ${editingFloor.floorName}`}
                        </h3>
                        <button
                            style={modalCloseButtonStyle}
                            onClick={() => {
                                setShowFloorEditModal(false);
                                setEditingFloor(null);
                                setFocusedCapacity(null);
                            }}
                            title="Close"
                        >
                            <FaTimes />
                        </button>
                    </div>
                    {!focusedCapacity && (
                        <>
                            <div style={inputContainerStyle}>
                                <label htmlFor="editFloorName" style={labelStyle}>
                                    Floor Name:<span style={{ color: "red" }}>*</span>
                                </label>
                                <input
                                    id="editFloorName"
                                    type="text"
                                    value={editingFloor.floorName}
                                    onChange={(e) =>
                                        setEditingFloor({
                                            ...editingFloor,
                                            floorName: e.target.value,
                                        })
                                    }
                                    style={inputFieldStyle}
                                />
                            </div>
                            <div style={inputContainerStyle}>
                                <label htmlFor="floorCleaningDuration" style={labelStyle}>
                                    Default Cleaning Duration (min):
                                    <span style={{ color: "red" }}>*</span>
                                </label>
                                <input
                                    id="floorCleaningDuration"
                                    type="number"
                                    min="0"
                                    value={
                                        editingFloor.defaultCleaningDuration === undefined
                                            ? ""
                                            : String(editingFloor.defaultCleaningDuration)
                                    }
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        setEditingFloor({
                                            ...editingFloor,
                                            defaultCleaningDuration:
                                                val === ""
                                                    ? undefined
                                                    : parseInt(val, 10) >= 0
                                                        ? parseInt(val, 10)
                                                        : 0,
                                        });
                                    }}
                                    style={inputFieldStyle}
                                    placeholder={`e.g., 15`}
                                />
                            </div>
                        </>
                    )}
                    <div
                        style={{
                            marginTop: "20px",
                            borderTop: "1px solid #eee",
                            paddingTop: "15px",
                        }}
                    >
                        <h4 style={{ margin: "0 0 10px 0", fontSize: "16px" }}>
                            {focusedCapacity
                                ? `Manage Tables for Capacity: ${focusedCapacity}`
                                : "Table Configurations (by Capacity):"}
                        </h4>
                        {editingFloor.configurations.length === 0 && !focusedCapacity && (
                            <p
                                style={{
                                    fontSize: "13px",
                                    color: "#555",
                                    textAlign: "center",
                                }}
                            >
                                No table configurations defined. Add one below.
                            </p>
                        )}
                        {(focusedCapacity
                            ? editingFloor.configurations.filter(
                                (c) => c.capacity === focusedCapacity
                            )
                            : editingFloor.configurations
                        )
                            .sort((a, b) => parseInt(a.capacity) - parseInt(b.capacity))
                            .map((config) => {
                                if (focusedCapacity && config.capacity !== focusedCapacity) {
                                    return null;
                                }
                                return (
                                    <div key={config.id} style={configSectionStyle}>
                                        <div style={configHeaderStyle}>
                                            <span>
                                                Capacity: <strong>{config.capacity}</strong> (
                                                {config.individualTables.length} Table
                                                {config.individualTables.length === 1 ? "" : "s"})
                                            </span>
                                        </div>
                                        {config.individualTables.length > 0 ? (
                                            <div style={individualTableListStyleInModal}>
                                                {config.individualTables.map((table) => (
                                                    <div
                                                        key={table.id}
                                                        style={individualTableEditRowStyle}
                                                    >
                                                        <span style={{ minWidth: "70px" }}>
                                                            {getShortTableIdentifier(table.id)}
                                                        </span>
                                                        <span style={{
                                                            marginLeft: '10px',
                                                            marginRight: 'auto',
                                                            padding: '4px 8px',
                                                            backgroundColor: '#f0f0f0',
                                                            borderRadius: '4px',
                                                            fontSize: '12px',
                                                            color: '#333'
                                                        }}>
                                                            Status: <strong>{getDisplayStatus(table.seatingStatus)}</strong>
                                                        </span>
                                                        <button
                                                            onClick={() =>
                                                                handleRemoveIndividualTableFromEditingFloorConfig(
                                                                    config.id,
                                                                    table.id
                                                                )
                                                            }
                                                            style={{
                                                                ...actionButtonStyle,
                                                                fontSize: "13px",
                                                                color: "#c82333",
                                                                padding: "3px 5px",
                                                            }}
                                                            title="Delete this Table"
                                                        >
                                                            <FaTrash />
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <p
                                                style={{
                                                    fontSize: "12px",
                                                    color: "#666",
                                                    paddingLeft: "10px",
                                                    margin: "5px 0",
                                                }}
                                            >
                                                No tables in this configuration.
                                            </p>
                                        )}
                                    </div>
                                );
                            })}
                        {focusedCapacity &&
                            !editingFloor.configurations.some(
                                (c) => c.capacity === focusedCapacity
                            ) && (
                                <p
                                    style={{
                                        fontSize: "13px",
                                        color: "#777",
                                        textAlign: "center",
                                        marginTop: "10px",
                                    }}
                                >
                                    Configuration for capacity "{focusedCapacity}" does not
                                    exist. Add it via general floor edit.
                                </p>
                            )}
                        {!focusedCapacity && (
                            <div
                                style={{
                                    marginTop: "20px",
                                    borderTop: "1px dashed #ccc",
                                    paddingTop: "15px",
                                }}
                            >
                                <h5 style={{ margin: "0 0 10px 0", fontSize: "15px" }}>
                                    Add New Configuration Group:
                                </h5>
                                <div style={inputRowStyle}>
                                    <div style={inputContainerHalfStyle}>
                                        <label htmlFor="newConfigCapacity" style={labelStyle}>
                                            Capacity:<span style={{ color: "red" }}>*</span>
                                        </label>
                                        <input
                                            id="newConfigCapacity"
                                            type="number"
                                            min="1"
                                            value={newConfigCapacity}
                                            onChange={(e) => setNewConfigCapacity(e.target.value)}
                                            style={inputFieldStyle}
                                            placeholder="e.g., 2, 4"
                                        />
                                    </div>
                                    <div style={inputContainerHalfStyle}>
                                        <label htmlFor="newConfigNumTables" style={labelStyle}>
                                            # Tables for this Capacity:
                                            <span style={{ color: "red" }}>*</span>
                                        </label>
                                        <input
                                            id="newConfigNumTables"
                                            type="number"
                                            min="1"
                                            value={newConfigNumTables}
                                            onChange={(e) => setNewConfigNumTables(e.target.value)}
                                            style={inputFieldStyle}
                                            placeholder="e.g., 5"
                                        />
                                    </div>
                                </div>
                                <button
                                    onClick={handleAddConfigurationToEditingFloor}
                                    style={{
                                        ...buttonStyle,
                                        height: "32px",
                                        fontSize: "13px",
                                        backgroundColor: "#17a2b8",
                                        color: "white",
                                        padding: "0 10px",
                                    }}
                                    title="Add Configuration Group"
                                    disabled={isPosting}
                                >
                                    {isPosting ? (
                                        "Adding..."
                                    ) : (
                                        <>
                                            <FaLayerGroup style={{ marginRight: "5px" }} /> Add
                                            Config Group
                                        </>
                                    )}
                                </button>
                            </div>
                        )}
                    </div>
                    <div style={buttonContainerStyle}>
                    </div>
                </div>
            )}
            {showBookTableModal && bookingTableInfo && (
                <div style={{ ...modalStyle, maxWidth: "450px" }}>
                    <div style={modalHeaderStyle}>
                        <h3 style={{ margin: 0, fontSize: "18px" }}>
                            Book Table (Cap: {bookingTableInfo.capacity})
                        </h3>
                        <button
                            style={modalCloseButtonStyle}
                            onClick={() => {
                                setShowBookTableModal(false);
                                setBookingTableInfo(null);
                            }}
                            title="Close"
                        >
                            <FaTimes />
                        </button>
                    </div>
                    <p style={{ marginBottom: "15px", fontSize: "14px" }}>
                        Table ID:{" "}
                        <strong>
                            {getShortTableIdentifier(bookingTableInfo.tableCode)}
                        </strong>{" "}
                        (Floor:{" "}
                        {data.find((f) => f.id === bookingTableInfo.floorId)?.floorName})
                    </p>
                    <div style={inputRowStyle}>
                        <div style={inputContainerHalfStyle}>
                            <label htmlFor="bookingCustomerName" style={labelStyle}>
                                Name:<span style={{ color: "red" }}>*</span>
                            </label>
                            <input
                                id="bookingCustomerName"
                                type="text"
                                value={bookingCustomerName}
                                onChange={(e) => setBookingCustomerName(e.target.value)}
                                style={inputFieldStyle}
                            />
                        </div>
                        <div style={inputContainerHalfStyle}>
                            <label htmlFor="bookingPhoneNumber" style={labelStyle}>
                                Phone:
                            </label>
                            <input
                                id="bookingPhoneNumber"
                                type="tel"
                                value={bookingPhoneNumber}
                                onChange={(e) => setBookingPhoneNumber(e.target.value)}
                                style={inputFieldStyle}
                            />
                        </div>
                    </div>
                    <div style={inputRowStyle}>
                        <div style={inputContainerHalfStyle}>
                            <label htmlFor="bookingDate" style={labelStyle}>
                                Date:<span style={{ color: "red" }}>*</span>
                            </label>
                            <input
                                id="bookingDate"
                                type="date"
                                value={bookingDate}
                                onChange={(e) => setBookingDate(e.target.value)}
                                style={inputFieldStyle}
                                min={getTodayDate()}
                            />
                        </div>
                        <div style={inputContainerHalfStyle}></div>
                    </div>
                    <div style={inputRowStyle}>
                        <div style={inputContainerHalfStyle}>
                            <label htmlFor="bookingStartTime" style={labelStyle}>
                                Start Time (HH:MM):<span style={{ color: "red" }}>*</span>
                            </label>
                            <input
                                id="bookingStartTime"
                                type="time"
                                value={bookingStartTime}
                                onChange={(e) => setBookingStartTime(e.target.value)}
                                style={inputFieldStyle}
                            />
                        </div>
                        <div style={inputContainerHalfStyle}>
                            <label htmlFor="bookingDurationMinutes" style={labelStyle}>
                                Duration (minutes):<span style={{ color: "red" }}>*</span>
                            </label>
                            <input
                                id="bookingDurationMinutes"
                                type="number"
                                min="15"
                                step="15"
                                value={bookingDurationMinutes}
                                onChange={(e) => setBookingDurationMinutes(e.target.value)}
                                style={inputFieldStyle}
                                placeholder={`e.g., 90`}
                            />
                        </div>
                    </div>
                    <p
                        style={{
                            fontSize: "11px",
                            color: "#555",
                            marginTop: "-8px",
                            marginBottom: "10px",
                        }}
                    >
                        Start Time and a positive Duration are required.
                    </p>
                    <p
                        style={{
                            fontSize: "11px",
                            color: "#333",
                            marginTop: "-8px",
                            marginBottom: "10px",
                        }}
                    >
                        Restaurant Hours: {RESTAURANT_OPEN_TIME_STR} -{" "}
                        {RESTAURANT_CLOSE_TIME_STR}{" "}
                        {timeToMinutes(RESTAURANT_CLOSE_TIME_STR) <
                            timeToMinutes(RESTAURANT_OPEN_TIME_STR)
                            ? "(next day)"
                            : "(same day)"}
                        .
                    </p>
                    <div style={inputContainerStyle}>
                        <label htmlFor="bookingNotes" style={labelStyle}>
                            Notes:
                        </label>
                        <textarea
                            id="bookingNotes"
                            value={bookingNotes}
                            onChange={(e) => setBookingNotes(e.target.value)}
                            style={{
                                ...inputFieldStyle,
                                height: "60px",
                                resize: "vertical",
                            }}
                        />
                    </div>
                    <div style={buttonContainerStyle}>
                        <button
                            onClick={handleConfirmBooking}
                            style={{
                                ...buttonStyle,
                                width: "180px",
                                backgroundColor: statusColors.Reserved,
                            }}
                        >
                            <FaBookmark style={{ marginRight: "8px" }} /> Confirm Booking
                        </button>
                    </div>
                </div>
            )}

            {showIndividualTableModal && editingTableInfo && (
                <div style={{ ...modalStyle, maxWidth: "450px" }}>
                    <div style={modalHeaderStyle}>
                        <h3 style={{ margin: 0, fontSize: "18px" }}>
                            Manage Table (Cap: {editingTableInfo.capacity})
                        </h3>
                        <button
                            style={modalCloseButtonStyle}
                            onClick={() => {
                                setShowIndividualTableModal(false);
                                setEditingTableInfo(null);
                            }}
                            title="Close"
                        >
                            <FaTimes />
                        </button>
                    </div>
                    <p style={{ marginBottom: "15px", fontSize: "14px" }}>
                        Table ID: <strong>{getShortTableIdentifier(editingTableInfo.tableId)}</strong>
                    </p>
                    <div style={detailsBoxStyle}>
                        <strong>Current Booking Details:</strong>
                        {(() => {
                            const floorObj = data.find(f => f.id === editingTableInfo.floorId);
                            const configObj = floorObj?.configurations.find(c => c.id === editingTableInfo.configId);
                            const tableObj = configObj?.individualTables.find(t => t.id === editingTableInfo.tableId);
                            if (!tableObj || !Array.isArray(tableObj.bookings) || tableObj.bookings.length === 0) {
                                return <div style={{ fontSize: "13px", color: "#777" }}>No bookings for this table.</div>;
                            }
                            const nowMinutes = timeToMinutes(actualCurrentTime);
                            const today = actualCurrentDate;
                            let currentBooking = null;
                            let nextBooking = null;
                            const todayBookings = tableObj.bookings
                                .filter(b => b.date === today)
                                .sort((a, b) => (timeToMinutes(a.time) ?? 0) - (timeToMinutes(b.time) ?? 0));
                            for (const b of todayBookings) {
                                const start = timeToMinutes(b.time);
                                const end = timeToMinutes(b.endTime);
                                if (start !== null && end !== null) {
                                    if (nowMinutes >= start && nowMinutes < end) {
                                        currentBooking = b;
                                        break;
                                    }
                                    if (nowMinutes < start && !nextBooking) {
                                        nextBooking = b;
                                    }
                                }
                            }
                            const showBooking = currentBooking || nextBooking || todayBookings[0];
                            if (!showBooking) {
                                return <div style={{ fontSize: "13px", color: "#777" }}>No bookings for this table.</div>;
                            }
                            return (
                                <div style={{ fontSize: "13px", color: "#333" }}>
                                    <div>
                                        <strong>Booking ID:</strong> {showBooking.id ?? "N/A"}
                                    </div>
                                    <div>
                                        <strong>Status:</strong> {editingTableInfo.currentStatus || "N/A"}
                                    </div>
                                    <div>
                                        <strong>Guest Name:</strong> {showBooking.name || showBooking.guestName || "N/A"}
                                    </div>
                                    <div>
                                        <strong>Phone:</strong> {showBooking.phone || showBooking.phoneNumber || "N/A"}
                                    </div>
                                    <div>
                                        <strong>Date:</strong> {showBooking.date || showBooking.bookingDate || "N/A"}
                                    </div>
                                    <div>
                                        <strong>Time:</strong> {showBooking.time || "N/A"} - {showBooking.endTime || "N/A"}
                                    </div>
                                </div>
                            );
                        })()}
                    </div>

                    <div style={inputContainerStyle}>
                        <label htmlFor="editTableStatus" style={labelStyle}>
                            Set New Table Status:
                        </label>
                        <select
                            id="editTableStatus"
                            name="newStatus"
                            value={editingTableInfo.newStatus}
                            onChange={handleIndividualTableStatusChange}
                            style={inputFieldStyle}
                        >
                            {tableStatuses.map((status) => (
                                <option key={status} value={status}>
                                    {status}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div style={buttonContainerStyle}>
                        <button
                            onClick={submitIndividualTableStatus}
                            style={{ ...buttonStyle, width: "180px" }}
                        >
                            <FaSave style={{ marginRight: "8px" }} />
                            Update Status
                        </button>
                    </div>
                </div>
            )}

            {showAddToWaitlistModal && (
                <div
                    style={{
                        position: "fixed",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        backgroundColor: "#fff",
                        padding: "24px",
                        borderRadius: "8px",
                        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                        zIndex: 9999,
                        width: "100%",
                        maxWidth: "500px",
                    }}
                >
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            marginBottom: "16px",
                        }}
                    >
                        <h3 style={{ margin: 0, fontSize: "18px" }}>
                            Add Guest to Waitlist
                        </h3>
                        <button
                            onClick={() => setShowAddToWaitlistModal(false)}
                            title="Close"
                            style={{
                                background: "transparent",
                                border: "none",
                                fontSize: "18px",
                                cursor: "pointer",
                            }}
                        >
                            <FaTimes />
                        </button>
                    </div>

                    <div style={{ marginBottom: "16px" }}>
                        <label
                            htmlFor="filterFloorWaitlist"
                            style={{
                                fontWeight: "bold",
                                marginBottom: "4px",
                                display: "block",
                            }}
                        >
                            <FaLayerGroup style={{ marginRight: "4px" }} />
                            Floor: <span style={{ color: "red" }}>*</span>
                        </label>
                        <select
                            id="filterFloorWaitlist"
                            value={waitlistModalFloorId}
                            onChange={(e) => setWaitlistModalFloorId(e.target.value)}
                            style={{
                                width: "100%",
                                padding: "8px",
                                border: "1px solid #ccc",
                                borderRadius: "4px",
                                fontSize: "14px",
                            }}
                        >
                            <option value="">Select Floor</option>
                            {data
                                .sort((a, b) => a.floorName.localeCompare(b.floorName))
                                .map((floor) => (
                                    <option key={floor.id} value={floor.id}>
                                        {floor.floorName}
                                    </option>
                                ))}
                        </select>
                    </div>

                    <div style={{ marginBottom: "16px" }}>
                        <label
                            htmlFor="waitlistGuestName"
                            style={{
                                fontWeight: "bold",
                                display: "block",
                                marginBottom: "4px",
                            }}
                        >
                            Guest Name: <span style={{ color: "red" }}>*</span>
                        </label>
                        <input
                            id="waitlistGuestName"
                            type="text"
                            value={newWaitlistEntryName}
                            onChange={(e) => setNewWaitlistEntryName(e.target.value)}
                            style={{
                                width: "100%",
                                padding: "8px",
                                border: "1px solid #ccc",
                                borderRadius: "4px",
                                fontSize: "14px",
                            }}
                        />
                    </div>

                    <div
                        style={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "16px",
                            marginBottom: "16px",
                        }}
                    >
                        <div style={{ flex: "1 1 45%" }}>
                            <label
                                htmlFor="waitlistPartySize"
                                style={{
                                    fontWeight: "bold",
                                    display: "block",
                                    marginBottom: "4px",
                                }}
                            >
                                Party Size: <span style={{ color: "red" }}>*</span>
                            </label>
                            <input
                                id="waitlistPartySize"
                                type="number"
                                min="1"
                                value={newWaitlistEntryPartySize}
                                onChange={(e) => setNewWaitlistEntryPartySize(e.target.value)}
                                style={{
                                    width: "100%",
                                    padding: "8px",
                                    border: "1px solid #ccc",
                                    borderRadius: "4px",
                                    fontSize: "14px",
                                }}
                            />
                        </div>

                        <div style={{ flex: "1 1 45%" }}>
                            <label
                                htmlFor="waitlistGuestPhone"
                                style={{
                                    fontWeight: "bold",
                                    display: "block",
                                    marginBottom: "4px",
                                }}
                            >
                                Phone (Optional):
                            </label>
                            <input
                                id="waitlistGuestPhone"
                                type="tel"
                                value={newWaitlistEntryPhone}
                                onChange={(e) => setNewWaitlistEntryPhone(e.target.value)}
                                style={{
                                    width: "100%",
                                    padding: "8px",
                                    border: "1px solid #ccc",
                                    borderRadius: "4px",
                                    fontSize: "14px",
                                }}
                            />
                        </div>

                        <div style={{ flex: "1 1 45%" }}>
                            <label
                                htmlFor="bookingDate"
                                style={{
                                    fontWeight: "bold",
                                    display: "block",
                                    marginBottom: "4px",
                                }}
                            >
                                Date: <span style={{ color: "red" }}>*</span>
                            </label>
                            <input
                                id="bookingDate"
                                type="date"
                                value={bookingDate}
                                onChange={(e) => setBookingDate(e.target.value)}
                                min={getTodayDate()}
                                style={{
                                    width: "100%",
                                    padding: "8px",
                                    border: "1px solid #ccc",
                                    borderRadius: "4px",
                                    fontSize: "14px",
                                }}
                            />
                        </div>

                        <div style={{ flex: "1 1 45%" }}>
                            <label
                                htmlFor="bookingDurationMinutes"
                                style={{
                                    fontWeight: "bold",
                                    display: "block",
                                    marginBottom: "4px",
                                }}
                            >
                                Duration (minutes): <span style={{ color: "red" }}>*</span>
                            </label>
                            <input
                                id="bookingDurationMinutes"
                                type="number"
                                min="15"
                                step="15"
                                value={bookingDurationMinutes}
                                onChange={(e) => setBookingDurationMinutes(e.target.value)}
                                placeholder={`e.g., 90`}
                                style={{
                                    width: "100%",
                                    padding: "8px",
                                    border: "1px solid #ccc",
                                    borderRadius: "4px",
                                    fontSize: "14px",
                                }}
                            />
                        </div>
                    </div>

                    <div style={{ display: "flex", justifyContent: "center" }}>
                        <button
                            onClick={handleSaveWaitlistEntry}
                            style={{
                                backgroundColor: "#17a2b8",
                                color: "#fff",
                                padding: "10px 16px",
                                border: "none",
                                borderRadius: "4px",
                                cursor: "pointer",
                                fontSize: "14px",
                                width: "180px",
                            }}
                        >
                            <FaUserPlus style={{ marginRight: "8px" }} />
                            Add to Waitlist
                        </button>
                    </div>
                </div>
            )}

            {showSeatWaitlistGuestModal && seatingWaitlistGuestInfo && (
                <div style={{ ...modalStyle, maxWidth: "500px" }}>
                    <div style={modalHeaderStyle}>
                        <h3 style={{ margin: 0, fontSize: "18px" }}>
                            Seat Waitlisted Guest
                        </h3>
                        <button
                            style={modalCloseButtonStyle}
                            onClick={() => {
                                setShowSeatWaitlistGuestModal(false);
                                setSeatingWaitlistGuestInfo(null);
                            }}
                            title="Close"
                        >
                            <FaTimes />
                        </button>
                    </div>
                    <div style={detailsBoxStyle}>
                        <p>
                            <strong>Guest:</strong> {seatingWaitlistGuestInfo.entry.guestName}
                        </p>
                        <p>
                            <strong>Party Size:</strong>{" "}
                            {seatingWaitlistGuestInfo.entry.capacity}
                        </p>
                        <p>
                            <strong>Phone:</strong>{" "}
                            {seatingWaitlistGuestInfo.entry.phoneNumber || "N/A"}
                        </p>
                    </div>

                    <div style={inputContainerStyle}>
                        <label htmlFor="selectTableForWaitlist" style={labelStyle}>
                            Select Available Table (Capacity {">="}{" "}
                            {seatingWaitlistGuestInfo.entry.capacity}):
                        </label>
                        {availableTablesForWaitlistGuest.length > 0 ? (
                            <select
                                id="selectTableForWaitlist"
                                value={selectedTableForWaitlistSeating}
                                onChange={(e) =>
                                    setSelectedTableForWaitlistSeating(e.target.value)
                                }
                                style={inputFieldStyle}
                            >
                                <option value="">-- Select a Table --</option>
                                {availableTablesForWaitlistGuest.map((table) => (
                                    <option key={table.id} value={table.id}>
                                        {table.shortId} (Cap: {table.capacityValue}, Floor:{" "}
                                        {table.floorName})
                                    </option>
                                ))}
                            </select>
                        ) : (
                            <p
                                style={{
                                    color: statusColors.Occupied,
                                    fontSize: "13px",
                                    marginTop: "5px",
                                }}
                            >
                                No suitable tables are currently available based on party
                                size.
                            </p>
                        )}
                    </div>
                    <p
                        style={{
                            fontSize: "11px",
                            color: "#333",
                            marginTop: "-8px",
                            marginBottom: "10px",
                        }}
                    >
                        Seating at: {actualCurrentDate} {actualCurrentTime} for{" "}
                        {90} mins (Restaurant Hours:{" "}
                        {RESTAURANT_OPEN_TIME_STR} - {RESTAURANT_CLOSE_TIME_STR}
                        ).
                    </p>

                    <div style={buttonContainerStyle}>
                        <button
                            onClick={handleConfirmSeatWaitlistGuest}
                            style={{
                                ...buttonStyle,
                                width: "180px",
                                backgroundColor: statusColors.Available,
                            }}
                            disabled={
                                !selectedTableForWaitlistSeating ||
                                availableTablesForWaitlistGuest.length === 0
                            }
                        >
                            <FaBookmark style={{ marginRight: "8px" }} /> Seat Guest
                        </button>
                    </div>
                </div>
            )}

            {(showFloorEditModal ||
                showBookTableModal ||
                showIndividualTableModal ||
                showAddToWaitlistModal ||
                showSeatWaitlistGuestModal) && (
                    <div
                        style={{
                            position: "fixed",
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: "rgba(0,0,0,0.5)",
                            zIndex: 999,
                        }}
                    />
                )}
        </div>
    );
    // return (
    //     <div className="container-fluid">
    //         <style>
    //             {`
    //             @media screen and (max-width: 768px) {
    //                 .rows {
    //                     margin-top: 0px !important;
    //                 }
    //                 .main-content-row {
    //                     flex-direction: column;
    //                 }
    //             }
    //         `}
    //         </style>

    //         {/* TOP SECTION: GLOBAL CONTROLS */}
    //         <div className="row rows">
    //             <div className="col-12">
    //                 <div style={globalControlsContainerStyle} className="obr">
    //                     {/* Left side: Filters */}
    //                     <div style={{ display: "flex", gap: "15px", alignItems: "center", flex: 1,flexWrap:"wrap" }}>
    //                         <div style={globalControlItemStyle}>
    //                             <label htmlFor="globalDate" style={labelStyle}>
    //                                 <FaCalendarAlt /> Date:
    //                             </label>
    //                             <input
    //                                 type="date"
    //                                 id="globalDate"
    //                                 value={globalSelectedDate}
    //                                 onChange={(e) => setGlobalSelectedDate(e.target.value)}
    //                                 style={{
    //                                     ...inputFieldStyle,
    //                                     width: "auto",
    //                                     padding: "6px 8px",
    //                                 }}
    //                             />
    //                         </div>
    //                         <div style={globalControlItemStyle}>
    //                             <label htmlFor="globalTime" style={labelStyle}>
    //                                 <FaClock /> Time:
    //                             </label>
    //                             <input
    //                                 type="time"
    //                                 id="globalTime"
    //                                 value={globalSelectedTime}
    //                                 onChange={(e) =>
    //                                     setGlobalSelectedTime(e.target.value || "")
    //                                 }
    //                                 style={{
    //                                     ...inputFieldStyle,
    //                                     width: "auto",
    //                                     padding: "6px 8px",
    //                                 }}
    //                             />
    //                         </div>
    //                         <div style={globalControlItemStyle}>
    //                             <label htmlFor="filterFloor" style={labelStyle}>
    //                                 <FaLayerGroup style={{ marginRight: "3px" }} /> Floor:
    //                             </label>
    //                             <select
    //                                 id="filterFloor"
    //                                 value={selectedFilterFloorId}
    //                                 onChange={(e) => setSelectedFilterFloorId(e.target.value)}
    //                                 style={{
    //                                     ...inputFieldStyle,
    //                                     width: "150px",
    //                                     padding: "6px 8px",
    //                                 }}
    //                             >
    //                                 <option value="">All Floors</option>
    //                                 {data
    //                                     .sort((a, b) => a.floorName.localeCompare(b.floorName))
    //                                     .map((floor) => (
    //                                         <option key={floor.id} value={floor.id}>
    //                                             {floor.floorName}
    //                                         </option>
    //                                     ))}
    //                             </select>
    //                         </div>
    //                     </div>

    //                     {/* Right side: Buttons */}
    //                     <div style={{ display: "flex", gap: "10px", alignItems: "center" ,flexDirection:window.innerWidth>500 && window.innerWidth<1500 ? "column" : " "}}>
    //                         <button
    //                             onClick={handleAddWaitlistGuestClick}
    //                             style={{
    //                                 ...buttonStyle,
    //                                 backgroundColor: "#17a2b8",
    //                             }}
    //                             title="Add guest to waitlist"
    //                         >
    //                             <FaUserPlus style={{ marginRight: "5px" }} /> Add to Waitlist
    //                         </button>
    //                         <button
    //                             onClick={handleAddFloorRow}
    //                             style={{
    //                                 ...buttonStyle,
    //                                 fontWeight: "bold",
    //                             }}
    //                             title="Add a new floor"
    //                         >
    //                             <FaPlus style={{ marginRight: "3px" }} />
    //                             Add Floor
    //                         </button>
    //                     </div>
    //                 </div>
    //                  <StatusLegend />
    //             </div>
    //         </div>

    //         {/* MAIN CONTENT ROW */}
    //         <div className="row rows">
    //             {/* LEFT COLUMN - MAIN TABLE */}
    //             <div className="col-lg-8 col-md-8 col-sm-12 col-12">
    //                 <div style={pageContainerStyle}>
    //                     <div style={mainTableSectionStyle}>


    //                         <div
    //                             style={{
    //                                 marginTop: "10px",
    //                                 flexGrow: 1,
    //                                 overflowY: "auto",
    //                                 height: "48vh",
    //                                 scrollbarWidth: "thin",
    //                             }}
    //                             className="rep"
    //                         >
    //                             {processedDataForDisplay.length === 0 ? (
    //                                 <p
    //                                     style={{
    //                                         textAlign: "center",
    //                                         marginTop: "20px",
    //                                         fontSize: "16px",
    //                                     }}
    //                                 >
    //                                     {data.length === 0
    //                                         ? "No floors available from API. Click 'Add Floor' to create one."
    //                                         : "No tables match the current filter criteria."}
    //                                 </p>
    //                             ) : (
    //                                 <>
    //                                     <div style={{}} className="rep2 pe-2">
    //                                         <table
    //                                             style={{ width: "100%", borderCollapse: "collapse" }}
    //                                             className="table"
    //                                         >
    //                                             <thead>
    //                                                 <tr>
    //                                                     <th style={tableHeaderStyle}>S.No</th>
    //                                                     <th style={tableHeaderStyle}>Floor Name</th>
    //                                                     <th style={{ ...tableHeaderStyle, width: "80px" }}>
    //                                                         Capacity
    //                                                     </th>
    //                                                     <th style={{ ...tableHeaderStyle, width: "100px" }}>
    //                                                         Total Tables
    //                                                     </th>
    //                                                     <th style={{ ...tableHeaderStyle, width: "120px" }}>
    //                                                         Available
    //                                                     </th>
    //                                                     <th
    //                                                         style={{ ...tableHeaderStyle, minWidth: "250px" }}
    //                                                     >
    //                                                         Tables Status & Actions
    //                                                     </th>
    //                                                     <th style={{ ...tableHeaderStyle, width: "100px" }}>
    //                                                         Floor Actions
    //                                                     </th>
    //                                                     <th style={{ ...tableHeaderStyle, width: "100px" }}>
    //                                                         tableId
    //                                                     </th>
    //                                                 </tr>
    //                                             </thead>
    //                                             <tbody>
    //                                                 {processedDataForDisplay.map((rowItem, index) => {
    //                                                     const stats = getRowTableStats(rowItem);
    //                                                     return (
    //                                                         <tr
    //                                                             key={rowItem.uniqueRowId}
    //                                                             style={index % 2 === 0 ? evenRowStyle : oddRowStyle}
    //                                                         >
    //                                                             <td style={tableCellStyle}>{index + 1}</td>
    //                                                             <td style={tableCellStyle}>
    //                                                                 {rowItem.floor.floorName}
    //                                                             </td>
    //                                                             <td style={tableCellStyle}>
    //                                                                 {rowItem.capacity}
    //                                                             </td>
    //                                                             <td style={tableCellStyle}>
    //                                                                 {stats.total}
    //                                                             </td>
    //                                                             <td style={tableCellStyle}>
    //                                                                 {stats.available}
    //                                                             </td>
    //                                                             <td
    //                                                                 style={{
    //                                                                     ...tableCellStyle,
    //                                                                     textAlign: "left",
    //                                                                     verticalAlign: "top",
    //                                                                 }}
    //                                                             >
    //                                                                 {rowItem.isPlaceholder ? (
    //                                                                     <span
    //                                                                         style={{
    //                                                                             fontSize: "12px",
    //                                                                             color: "#777",
    //                                                                         }}
    //                                                                     >
    //                                                                         No tables configured.
    //                                                                     </span>
    //                                                                 ) : rowItem.configurations.length > 0 &&
    //                                                                     stats.total > 0 ? (
    //                                                                     <div style={tableVisualContainerStyle}>
    //                                                                         {rowItem.configurations.flatMap(
    //                                                                             (config) =>
    //                                                                                 config.individualTables
    //                                                                                     .filter(
    //                                                                                         (table) =>
    //                                                                                             table.capacityValue ===
    //                                                                                             rowItem.capacity
    //                                                                                     )
    //                                                                                     .map((table) => {
    //                                                                                         const displayStatus = getComputedTableStatus(
    //                                                                                             table,
    //                                                                                             globalSelectedDate,
    //                                                                                             globalSelectedTime,
    //                                                                                             actualCurrentDate,
    //                                                                                             actualCurrentTime
    //                                                                                         );

    //                                                                                         const shortId = getShortTableIdentifier(table.code);
    //                                                                                         return (
    //                                                                                             <div
    //                                                                                                 key={table.id}
    //                                                                                                 style={
    //                                                                                                     tableRepresentationWrapperStyleNoHighlight
    //                                                                                                 }
    //                                                                                                 title={`Table ${shortId} (Cap: ${table.capacityValue}) - Status: ${getDisplayStatus(table.seatingStatus)}. Click to manage.`}
    //                                                                                             >
    //                                                                                                 <div
    //                                                                                                     style={{
    //                                                                                                         ...individualTableStyle,
    //                                                                                                         backgroundColor:
    //                                                                                                             statusColors[displayStatus] || statusColors["Maintenance"],
    //                                                                                                     }}
    //                                                                                                     onClick={() =>
    //                                                                                                         handleIndividualTableClick(
    //                                                                                                             rowItem.floor.id,
    //                                                                                                             config.id,
    //                                                                                                             table
    //                                                                                                         )
    //                                                                                                     }
    //                                                                                                 >
    //                                                                                                     {shortId}
    //                                                                                                 </div>
    //                                                                                             </div>
    //                                                                                         );
    //                                                                                     })
    //                                                                         )}
    //                                                                     </div>
    //                                                                 ) : (
    //                                                                     <span
    //                                                                         style={{
    //                                                                             fontSize: "12px",
    //                                                                             color: "#777",
    //                                                                         }}
    //                                                                     >
    //                                                                         No tables configured for capacity{" "}
    //                                                                         {rowItem.capacity}.
    //                                                                     </span>
    //                                                                 )}
    //                                                             </td>
    //                                                             <td style={tableCellStyle}>
    //                                                                 <button
    //                                                                     style={actionButtonStyle}
    //                                                                     onClick={() =>
    //                                                                         handleEditFloorSetup(
    //                                                                             rowItem.floor.id,
    //                                                                             rowItem.isPlaceholder
    //                                                                                 ? null
    //                                                                                 : rowItem.capacity
    //                                                                         )
    //                                                                     }
    //                                                                     title={`Edit Floor or Table Configurations`}
    //                                                                 >
    //                                                                     <FaEdit />
    //                                                                 </button>
    //                                                             </td>
    //                                                             <td style={{
    //                                                                 ...tableCellStyle,
    //                                                                 fontSize: '12px',
    //                                                                 wordBreak: 'break-word',
    //                                                                 maxWidth: '200px',
    //                                                                 minWidth: "150px",
    //                                                                 whiteSpace: 'normal',
    //                                                                 overflow: 'hidden',
    //                                                                 textOverflow: 'ellipsis'
    //                                                             }}>
    //                                                                 {
    //                                                                     rowItem.configurations
    //                                                                         .flatMap(config => config.individualTables.map(table => table.id))
    //                                                                         .join(', ')
    //                                                                 }
    //                                                             </td>
    //                                                         </tr>
    //                                                     );
    //                                                 })}
    //                                             </tbody>
    //                                         </table>
    //                                     </div>
    //                                 </>
    //                             )}
    //                         </div>
    //                     </div>
    //                 </div>
    //             </div>

    //             {/* RIGHT COLUMN - BOOKINGS AND WAITLIST */}
    //             <div
    //                 style={bookingsViewSectionStyle}
    //                 className="bookings-sidebar-scroll-area col-lg-4 col-md-4 col-sm-12 col-12"
    //             >
    //                 <AllBookingsView
    //                     tableHeaderStyle={tableHeaderStyle}
    //                     tableCellStyle={tableCellStyle}
    //                     evenRowStyle={evenRowStyle}
    //                     oddRowStyle={oddRowStyle}
    //                     paginationButtonStyle={paginationButtonStyle}
    //                 />
    //                 <WaitlistView
    //                     waitlist={waitlist}
    //                     onSeatGuestRequest={handleSeatGuestRequest}
    //                     onRemoveFromWaitlist={handleRemoveFromWaitlist}
    //                     tableHeaderStyle={tableHeaderStyle}
    //                     tableCellStyle={tableCellStyle}
    //                     evenRowStyle={evenRowStyle}
    //                     oddRowStyle={oddRowStyle}
    //                     paginationButtonStyle={paginationButtonStyle}
    //                     smallActionButtonStyle={smallActionButtonStyle}
    //                     statusColors={statusColors}
    //                     waitlistStatusColors={waitlistStatusColors}
    //                 />
    //             </div>
    //         </div>

    //         {/* --- ALL MODALS --- */}
    //         {showFloorEditModal && editingFloor && (
    //             <div style={{ ...modalStyle, maxWidth: "600px" }}>
    //                 <div style={modalHeaderStyle}>
    //                     <h3 style={{ margin: 0, fontSize: "18px" }}>
    //                         {editingFloor.isAdded === false
    //                             ? "Add New Floor"
    //                             : focusedCapacity
    //                                 ? `Edit Tables for Capacity ${focusedCapacity} on ${editingFloor.floorName}`
    //                                 : `Edit Floor: ${editingFloor.floorName}`}
    //                     </h3>
    //                     <button
    //                         style={modalCloseButtonStyle}
    //                         onClick={() => {
    //                             setShowFloorEditModal(false);
    //                             setEditingFloor(null);
    //                             setFocusedCapacity(null);
    //                         }}
    //                         title="Close"
    //                     >
    //                         <FaTimes />
    //                     </button>
    //                 </div>
    //                 {!focusedCapacity && (
    //                     <>
    //                         <div style={inputContainerStyle}>
    //                             <label htmlFor="editFloorName" style={labelStyle}>
    //                                 Floor Name:<span style={{ color: "red" }}>*</span>
    //                             </label>
    //                             <input
    //                                 id="editFloorName"
    //                                 type="text"
    //                                 value={editingFloor.floorName}
    //                                 onChange={(e) =>
    //                                     setEditingFloor({
    //                                         ...editingFloor,
    //                                         floorName: e.target.value,
    //                                     })
    //                                 }
    //                                 style={inputFieldStyle}
    //                             />
    //                         </div>
    //                         <div style={inputContainerStyle}>
    //                             <label htmlFor="floorCleaningDuration" style={labelStyle}>
    //                                 Default Cleaning Duration (min):
    //                                 <span style={{ color: "red" }}>*</span>
    //                             </label>
    //                             <input
    //                                 id="floorCleaningDuration"
    //                                 type="number"
    //                                 min="0"
    //                                 value={
    //                                     editingFloor.defaultCleaningDuration === undefined
    //                                         ? ""
    //                                         : String(editingFloor.defaultCleaningDuration)
    //                                 }
    //                                 onChange={(e) => {
    //                                     const val = e.target.value;
    //                                     setEditingFloor({
    //                                         ...editingFloor,
    //                                         defaultCleaningDuration:
    //                                             val === ""
    //                                                 ? undefined
    //                                                 : parseInt(val, 10) >= 0
    //                                                     ? parseInt(val, 10)
    //                                                     : 0,
    //                                     });
    //                                 }}
    //                                 style={inputFieldStyle}
    //                                 placeholder={`e.g., 15`}
    //                             />
    //                         </div>
    //                     </>
    //                 )}
    //                 <div
    //                     style={{
    //                         marginTop: "20px",
    //                         borderTop: "1px solid #eee",
    //                         paddingTop: "15px",
    //                     }}
    //                 >
    //                     <h4 style={{ margin: "0 0 10px 0", fontSize: "16px" }}>
    //                         {focusedCapacity
    //                             ? `Manage Tables for Capacity: ${focusedCapacity}`
    //                             : "Table Configurations (by Capacity):"}
    //                     </h4>
    //                     {editingFloor.configurations.length === 0 && !focusedCapacity && (
    //                         <p
    //                             style={{
    //                                 fontSize: "13px",
    //                                 color: "#555",
    //                                 textAlign: "center",
    //                             }}
    //                         >
    //                             No table configurations defined. Add one below.
    //                         </p>
    //                     )}
    //                     {(focusedCapacity
    //                         ? editingFloor.configurations.filter(
    //                             (c) => c.capacity === focusedCapacity
    //                         )
    //                         : editingFloor.configurations
    //                     )
    //                         .sort((a, b) => parseInt(a.capacity) - parseInt(b.capacity))
    //                         .map((config) => {
    //                             if (focusedCapacity && config.capacity !== focusedCapacity) {
    //                                 return null;
    //                             }
    //                             return (
    //                                 <div key={config.id} style={configSectionStyle}>
    //                                     <div style={configHeaderStyle}>
    //                                         <span>
    //                                             Capacity: <strong>{config.capacity}</strong> (
    //                                             {config.individualTables.length} Table
    //                                             {config.individualTables.length === 1 ? "" : "s"})
    //                                         </span>
    //                                     </div>
    //                                     {config.individualTables.length > 0 ? (
    //                                         <div style={individualTableListStyleInModal}>
    //                                             {config.individualTables.map((table) => (
    //                                                 <div
    //                                                     key={table.id}
    //                                                     style={individualTableEditRowStyle}
    //                                                 >
    //                                                     <span style={{ minWidth: "70px" }}>
    //                                                         {getShortTableIdentifier(table.id)}
    //                                                     </span>
    //                                                     <span style={{
    //                                                         marginLeft: '10px',
    //                                                         marginRight: 'auto',
    //                                                         padding: '4px 8px',
    //                                                         backgroundColor: '#f0f0f0',
    //                                                         borderRadius: '4px',
    //                                                         fontSize: '12px',
    //                                                         color: '#333'
    //                                                     }}>
    //                                                         Status: <strong>{getDisplayStatus(table.seatingStatus)}</strong>
    //                                                     </span>
    //                                                     <button
    //                                                         onClick={() =>
    //                                                             handleRemoveIndividualTableFromEditingFloorConfig(
    //                                                                 config.id,
    //                                                                 table.id
    //                                                             )
    //                                                         }
    //                                                         style={{
    //                                                             ...actionButtonStyle,
    //                                                             fontSize: "13px",
    //                                                             color: "#c82333",
    //                                                             padding: "3px 5px",
    //                                                         }}
    //                                                         title="Delete this Table"
    //                                                     >
    //                                                         <FaTrash />
    //                                                     </button>
    //                                                 </div>
    //                                             ))}
    //                                         </div>
    //                                     ) : (
    //                                         <p
    //                                             style={{
    //                                                 fontSize: "12px",
    //                                                 color: "#666",
    //                                                 paddingLeft: "10px",
    //                                                 margin: "5px 0",
    //                                             }}
    //                                         >
    //                                             No tables in this configuration.
    //                                         </p>
    //                                     )}
    //                                 </div>
    //                             );
    //                         })}
    //                     {focusedCapacity &&
    //                         !editingFloor.configurations.some(
    //                             (c) => c.capacity === focusedCapacity
    //                         ) && (
    //                             <p
    //                                 style={{
    //                                     fontSize: "13px",
    //                                     color: "#777",
    //                                     textAlign: "center",
    //                                     marginTop: "10px",
    //                                 }}
    //                             >
    //                                 Configuration for capacity "{focusedCapacity}" does not
    //                                 exist. Add it via general floor edit.
    //                             </p>
    //                         )}
    //                     {!focusedCapacity && (
    //                         <div
    //                             style={{
    //                                 marginTop: "20px",
    //                                 borderTop: "1px dashed #ccc",
    //                                 paddingTop: "15px",
    //                             }}
    //                         >
    //                             <h5 style={{ margin: "0 0 10px 0", fontSize: "15px" }}>
    //                                 Add New Configuration Group:
    //                             </h5>
    //                             <div style={inputRowStyle}>
    //                                 <div style={inputContainerHalfStyle}>
    //                                     <label htmlFor="newConfigCapacity" style={labelStyle}>
    //                                         Capacity:<span style={{ color: "red" }}>*</span>
    //                                     </label>
    //                                     <input
    //                                         id="newConfigCapacity"
    //                                         type="number"
    //                                         min="1"
    //                                         value={newConfigCapacity}
    //                                         onChange={(e) => setNewConfigCapacity(e.target.value)}
    //                                         style={inputFieldStyle}
    //                                         placeholder="e.g., 2, 4"
    //                                     />
    //                                 </div>
    //                                 <div style={inputContainerHalfStyle}>
    //                                     <label htmlFor="newConfigNumTables" style={labelStyle}>
    //                                         # Tables for this Capacity:
    //                                         <span style={{ color: "red" }}>*</span>
    //                                     </label>
    //                                     <input
    //                                         id="newConfigNumTables"
    //                                         type="number"
    //                                         min="1"
    //                                         value={newConfigNumTables}
    //                                         onChange={(e) => setNewConfigNumTables(e.target.value)}
    //                                         style={inputFieldStyle}
    //                                         placeholder="e.g., 5"
    //                                     />
    //                                 </div>
    //                             </div>
    //                             <button
    //                                 onClick={handleAddConfigurationToEditingFloor}
    //                                 style={{
    //                                     ...buttonStyle,
    //                                     height: "32px",
    //                                     fontSize: "13px",
    //                                     backgroundColor: "#17a2b8",
    //                                     color: "white",
    //                                     padding: "0 10px",
    //                                 }}
    //                                 title="Add Configuration Group"
    //                                 disabled={isPosting}
    //                             >
    //                                 {isPosting ? (
    //                                     "Adding..."
    //                                 ) : (
    //                                     <>
    //                                         <FaLayerGroup style={{ marginRight: "5px" }} /> Add
    //                                         Config Group
    //                                     </>
    //                                 )}
    //                             </button>
    //                         </div>
    //                     )}
    //                 </div>
    //                 <div style={buttonContainerStyle}>
    //                 </div>
    //             </div>
    //         )}
    //         {showBookTableModal && bookingTableInfo && (
    //             <div style={{ ...modalStyle, maxWidth: "450px" }}>
    //                 <div style={modalHeaderStyle}>
    //                     <h3 style={{ margin: 0, fontSize: "18px" }}>
    //                         Book Table (Cap: {bookingTableInfo.capacity})
    //                     </h3>
    //                     <button
    //                         style={modalCloseButtonStyle}
    //                         onClick={() => {
    //                             setShowBookTableModal(false);
    //                             setBookingTableInfo(null);
    //                         }}
    //                         title="Close"
    //                     >
    //                         <FaTimes />
    //                     </button>
    //                 </div>
    //                 <p style={{ marginBottom: "15px", fontSize: "14px" }}>
    //                     Table ID:{" "}
    //                     <strong>
    //                         {getShortTableIdentifier(bookingTableInfo.tableCode)}
    //                     </strong>{" "}
    //                     (Floor:{" "}
    //                     {data.find((f) => f.id === bookingTableInfo.floorId)?.floorName})
    //                 </p>
    //                 <div style={inputRowStyle}>
    //                     <div style={inputContainerHalfStyle}>
    //                         <label htmlFor="bookingCustomerName" style={labelStyle}>
    //                             Name:<span style={{ color: "red" }}>*</span>
    //                         </label>
    //                         <input
    //                             id="bookingCustomerName"
    //                             type="text"
    //                             value={bookingCustomerName}
    //                             onChange={(e) => setBookingCustomerName(e.target.value)}
    //                             style={inputFieldStyle}
    //                         />
    //                     </div>
    //                     <div style={inputContainerHalfStyle}>
    //                         <label htmlFor="bookingPhoneNumber" style={labelStyle}>
    //                             Phone:
    //                         </label>
    //                         <input
    //                             id="bookingPhoneNumber"
    //                             type="tel"
    //                             value={bookingPhoneNumber}
    //                             onChange={(e) => setBookingPhoneNumber(e.target.value)}
    //                             style={inputFieldStyle}
    //                         />
    //                     </div>
    //                 </div>
    //                 <div style={inputRowStyle}>
    //                     <div style={inputContainerHalfStyle}>
    //                         <label htmlFor="bookingDate" style={labelStyle}>
    //                             Date:<span style={{ color: "red" }}>*</span>
    //                         </label>
    //                         <input
    //                             id="bookingDate"
    //                             type="date"
    //                             value={bookingDate}
    //                             onChange={(e) => setBookingDate(e.target.value)}
    //                             style={inputFieldStyle}
    //                             min={getTodayDate()}
    //                         />
    //                     </div>
    //                     <div style={inputContainerHalfStyle}></div>
    //                 </div>
    //                 <div style={inputRowStyle}>
    //                     <div style={inputContainerHalfStyle}>
    //                         <label htmlFor="bookingStartTime" style={labelStyle}>
    //                             Start Time (HH:MM):<span style={{ color: "red" }}>*</span>
    //                         </label>
    //                         <input
    //                             id="bookingStartTime"
    //                             type="time"
    //                             value={bookingStartTime}
    //                             onChange={(e) => setBookingStartTime(e.target.value)}
    //                             style={inputFieldStyle}
    //                         />
    //                     </div>
    //                     <div style={inputContainerHalfStyle}>
    //                         <label htmlFor="bookingDurationMinutes" style={labelStyle}>
    //                             Duration (minutes):<span style={{ color: "red" }}>*</span>
    //                         </label>
    //                         <input
    //                             id="bookingDurationMinutes"
    //                             type="number"
    //                             min="15"
    //                             step="15"
    //                             value={bookingDurationMinutes}
    //                             onChange={(e) => setBookingDurationMinutes(e.target.value)}
    //                             style={inputFieldStyle}
    //                             placeholder={`e.g., 90`}
    //                         />
    //                     </div>
    //                 </div>
    //                 <p
    //                     style={{
    //                         fontSize: "11px",
    //                         color: "#555",
    //                         marginTop: "-8px",
    //                         marginBottom: "10px",
    //                     }}
    //                 >
    //                     Start Time and a positive Duration are required.
    //                 </p>
    //                 <p
    //                     style={{
    //                         fontSize: "11px",
    //                         color: "#333",
    //                         marginTop: "-8px",
    //                         marginBottom: "10px",
    //                     }}
    //                 >
    //                     Restaurant Hours: {RESTAURANT_OPEN_TIME_STR} -{" "}
    //                     {RESTAURANT_CLOSE_TIME_STR}{" "}
    //                     {timeToMinutes(RESTAURANT_CLOSE_TIME_STR) <
    //                         timeToMinutes(RESTAURANT_OPEN_TIME_STR)
    //                         ? "(next day)"
    //                         : "(same day)"}
    //                     .
    //                 </p>
    //                 <div style={inputContainerStyle}>
    //                     <label htmlFor="bookingNotes" style={labelStyle}>
    //                         Notes:
    //                     </label>
    //                     <textarea
    //                         id="bookingNotes"
    //                         value={bookingNotes}
    //                         onChange={(e) => setBookingNotes(e.target.value)}
    //                         style={{
    //                             ...inputFieldStyle,
    //                             height: "60px",
    //                             resize: "vertical",
    //                         }}
    //                     />
    //                 </div>
    //                 <div style={buttonContainerStyle}>
    //                     <button
    //                         onClick={handleConfirmBooking}
    //                         style={{
    //                             ...buttonStyle,
    //                             width: "180px",
    //                             backgroundColor: statusColors.Reserved,
    //                         }}
    //                     >
    //                         <FaBookmark style={{ marginRight: "8px" }} /> Confirm Booking
    //                     </button>
    //                 </div>
    //             </div>
    //         )}

    //         {showIndividualTableModal && editingTableInfo && (
    //             <div style={{ ...modalStyle, maxWidth: "450px" }}>
    //                 <div style={modalHeaderStyle}>
    //                     <h3 style={{ margin: 0, fontSize: "18px" }}>
    //                         Manage Table (Cap: {editingTableInfo.capacity})
    //                     </h3>
    //                     <button
    //                         style={modalCloseButtonStyle}
    //                         onClick={() => {
    //                             setShowIndividualTableModal(false);
    //                             setEditingTableInfo(null);
    //                         }}
    //                         title="Close"
    //                     >
    //                         <FaTimes />
    //                     </button>
    //                 </div>
    //                 <p style={{ marginBottom: "15px", fontSize: "14px" }}>
    //                     Table ID: <strong>{getShortTableIdentifier(editingTableInfo.tableId)}</strong>
    //                 </p>
    //                 <div style={detailsBoxStyle}>
    //                     <strong>Current Booking Details:</strong>
    //                     {(() => {
    //                         const floorObj = data.find(f => f.id === editingTableInfo.floorId);
    //                         const configObj = floorObj?.configurations.find(c => c.id === editingTableInfo.configId);
    //                         const tableObj = configObj?.individualTables.find(t => t.id === editingTableInfo.tableId);
    //                         if (!tableObj || !Array.isArray(tableObj.bookings) || tableObj.bookings.length === 0) {
    //                             return <div style={{ fontSize: "13px", color: "#777" }}>No bookings for this table.</div>;
    //                         }
    //                         const nowMinutes = timeToMinutes(actualCurrentTime);
    //                         const today = actualCurrentDate;
    //                         let currentBooking = null;
    //                         let nextBooking = null;
    //                         const todayBookings = tableObj.bookings
    //                             .filter(b => b.date === today)
    //                             .sort((a, b) => (timeToMinutes(a.time) ?? 0) - (timeToMinutes(b.time) ?? 0));
    //                         for (const b of todayBookings) {
    //                             const start = timeToMinutes(b.time);
    //                             const end = timeToMinutes(b.endTime);
    //                             if (start !== null && end !== null) {
    //                                 if (nowMinutes >= start && nowMinutes < end) {
    //                                     currentBooking = b;
    //                                     break;
    //                                 }
    //                                 if (nowMinutes < start && !nextBooking) {
    //                                     nextBooking = b;
    //                                 }
    //                             }
    //                         }
    //                         const showBooking = currentBooking || nextBooking || todayBookings[0];
    //                         if (!showBooking) {
    //                             return <div style={{ fontSize: "13px", color: "#777" }}>No bookings for this table.</div>;
    //                         }
    //                         return (
    //                             <div style={{ fontSize: "13px", color: "#333" }}>
    //                                 <div>
    //                                     <strong>Booking ID:</strong> {showBooking.id ?? "N/A"}
    //                                 </div>
    //                                 <div>
    //                                     <strong>Status:</strong> {editingTableInfo.currentStatus || "N/A"}
    //                                 </div>
    //                                 <div>
    //                                     <strong>Guest Name:</strong> {showBooking.name || showBooking.guestName || "N/A"}
    //                                 </div>
    //                                 <div>
    //                                     <strong>Phone:</strong> {showBooking.phone || showBooking.phoneNumber || "N/A"}
    //                                 </div>
    //                                 <div>
    //                                     <strong>Date:</strong> {showBooking.date || showBooking.bookingDate || "N/A"}
    //                                 </div>
    //                                 <div>
    //                                     <strong>Time:</strong> {showBooking.time || "N/A"} - {showBooking.endTime || "N/A"}
    //                                 </div>
    //                             </div>
    //                         );
    //                     })()}
    //                 </div>

    //                 <div style={inputContainerStyle}>
    //                     <label htmlFor="editTableStatus" style={labelStyle}>
    //                         Set New Table Status:
    //                     </label>
    //                     <select
    //                         id="editTableStatus"
    //                         name="newStatus"
    //                         value={editingTableInfo.newStatus}
    //                         onChange={handleIndividualTableStatusChange}
    //                         style={inputFieldStyle}
    //                     >
    //                         {tableStatuses.map((status) => (
    //                             <option key={status} value={status}>
    //                                 {status}
    //                             </option>
    //                         ))}
    //                     </select>
    //                 </div>

    //                 <div style={buttonContainerStyle}>
    //                     <button
    //                         onClick={submitIndividualTableStatus}
    //                         style={{ ...buttonStyle, width: "180px" }}
    //                     >
    //                         <FaSave style={{ marginRight: "8px" }} />
    //                         Update Status
    //                     </button>
    //                 </div>
    //             </div>
    //         )}

    //         {showAddToWaitlistModal && (
    //             <div
    //                 style={{
    //                     position: "fixed",
    //                     top: "50%",
    //                     left: "50%",
    //                     transform: "translate(-50%, -50%)",
    //                     backgroundColor: "#fff",
    //                     padding: "24px",
    //                     borderRadius: "8px",
    //                     boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
    //                     zIndex: 9999,
    //                     width: "100%",
    //                     maxWidth: "500px",
    //                 }}
    //             >
    //                 <div
    //                     style={{
    //                         display: "flex",
    //                         justifyContent: "space-between",
    //                         alignItems: "center",
    //                         marginBottom: "16px",
    //                     }}
    //                 >
    //                     <h3 style={{ margin: 0, fontSize: "18px" }}>
    //                         Add Guest to Waitlist
    //                     </h3>
    //                     <button
    //                         onClick={() => setShowAddToWaitlistModal(false)}
    //                         title="Close"
    //                         style={{
    //                             background: "transparent",
    //                             border: "none",
    //                             fontSize: "18px",
    //                             cursor: "pointer",
    //                         }}
    //                     >
    //                         <FaTimes />
    //                     </button>
    //                 </div>

    //                 <div style={{ marginBottom: "16px" }}>
    //                     <label
    //                         htmlFor="filterFloorWaitlist"
    //                         style={{
    //                             fontWeight: "bold",
    //                             marginBottom: "4px",
    //                             display: "block",
    //                         }}
    //                     >
    //                         <FaLayerGroup style={{ marginRight: "4px" }} />
    //                         Floor: <span style={{ color: "red" }}>*</span>
    //                     </label>
    //                     <select
    //                         id="filterFloorWaitlist"
    //                         value={waitlistModalFloorId}
    //                         onChange={(e) => setWaitlistModalFloorId(e.target.value)}
    //                         style={{
    //                             width: "100%",
    //                             padding: "8px",
    //                             border: "1px solid #ccc",
    //                             borderRadius: "4px",
    //                             fontSize: "14px",
    //                         }}
    //                     >
    //                         <option value="">Select Floor</option>
    //                         {data
    //                             .sort((a, b) => a.floorName.localeCompare(b.floorName))
    //                             .map((floor) => (
    //                                 <option key={floor.id} value={floor.id}>
    //                                     {floor.floorName}
    //                                 </option>
    //                             ))}
    //                     </select>
    //                 </div>

    //                 <div style={{ marginBottom: "16px" }}>
    //                     <label
    //                         htmlFor="waitlistGuestName"
    //                         style={{
    //                             fontWeight: "bold",
    //                             display: "block",
    //                             marginBottom: "4px",
    //                         }}
    //                     >
    //                         Guest Name: <span style={{ color: "red" }}>*</span>
    //                     </label>
    //                     <input
    //                         id="waitlistGuestName"
    //                         type="text"
    //                         value={newWaitlistEntryName}
    //                         onChange={(e) => setNewWaitlistEntryName(e.target.value)}
    //                         style={{
    //                             width: "100%",
    //                             padding: "8px",
    //                             border: "1px solid #ccc",
    //                             borderRadius: "4px",
    //                             fontSize: "14px",
    //                         }}
    //                     />
    //                 </div>

    //                 <div
    //                     style={{
    //                         display: "flex",
    //                         flexWrap: "wrap",
    //                         gap: "16px",
    //                         marginBottom: "16px",
    //                     }}
    //                 >
    //                     <div style={{ flex: "1 1 45%" }}>
    //                         <label
    //                             htmlFor="waitlistPartySize"
    //                             style={{
    //                                 fontWeight: "bold",
    //                                 display: "block",
    //                                 marginBottom: "4px",
    //                             }}
    //                         >
    //                             Party Size: <span style={{ color: "red" }}>*</span>
    //                         </label>
    //                         <input
    //                             id="waitlistPartySize"
    //                             type="number"
    //                             min="1"
    //                             value={newWaitlistEntryPartySize}
    //                             onChange={(e) => setNewWaitlistEntryPartySize(e.target.value)}
    //                             style={{
    //                                 width: "100%",
    //                                 padding: "8px",
    //                                 border: "1px solid #ccc",
    //                                 borderRadius: "4px",
    //                                 fontSize: "14px",
    //                             }}
    //                         />
    //                     </div>

    //                     <div style={{ flex: "1 1 45%" }}>
    //                         <label
    //                             htmlFor="waitlistGuestPhone"
    //                             style={{
    //                                 fontWeight: "bold",
    //                                 display: "block",
    //                                 marginBottom: "4px",
    //                             }}
    //                         >
    //                             Phone (Optional):
    //                         </label>
    //                         <input
    //                             id="waitlistGuestPhone"
    //                             type="tel"
    //                             value={newWaitlistEntryPhone}
    //                             onChange={(e) => setNewWaitlistEntryPhone(e.target.value)}
    //                             style={{
    //                                 width: "100%",
    //                                 padding: "8px",
    //                                 border: "1px solid #ccc",
    //                                 borderRadius: "4px",
    //                                 fontSize: "14px",
    //                             }}
    //                         />
    //                     </div>

    //                     <div style={{ flex: "1 1 45%" }}>
    //                         <label
    //                             htmlFor="bookingDate"
    //                             style={{
    //                                 fontWeight: "bold",
    //                                 display: "block",
    //                                 marginBottom: "4px",
    //                             }}
    //                         >
    //                             Date: <span style={{ color: "red" }}>*</span>
    //                         </label>
    //                         <input
    //                             id="bookingDate"
    //                             type="date"
    //                             value={bookingDate}
    //                             onChange={(e) => setBookingDate(e.target.value)}
    //                             min={getTodayDate()}
    //                             style={{
    //                                 width: "100%",
    //                                 padding: "8px",
    //                                 border: "1px solid #ccc",
    //                                 borderRadius: "4px",
    //                                 fontSize: "14px",
    //                             }}
    //                         />
    //                     </div>

    //                     <div style={{ flex: "1 1 45%" }}>
    //                         <label
    //                             htmlFor="bookingDurationMinutes"
    //                             style={{
    //                                 fontWeight: "bold",
    //                                 display: "block",
    //                                 marginBottom: "4px",
    //                             }}
    //                         >
    //                             Duration (minutes): <span style={{ color: "red" }}>*</span>
    //                         </label>
    //                         <input
    //                             id="bookingDurationMinutes"
    //                             type="number"
    //                             min="15"
    //                             step="15"
    //                             value={bookingDurationMinutes}
    //                             onChange={(e) => setBookingDurationMinutes(e.target.value)}
    //                             placeholder={`e.g., 90`}
    //                             style={{
    //                                 width: "100%",
    //                                 padding: "8px",
    //                                 border: "1px solid #ccc",
    //                                 borderRadius: "4px",
    //                                 fontSize: "14px",
    //                             }}
    //                         />
    //                     </div>
    //                 </div>

    //                 <div style={{ display: "flex", justifyContent: "center" }}>
    //                     <button
    //                         onClick={handleSaveWaitlistEntry}
    //                         style={{
    //                             backgroundColor: "#17a2b8",
    //                             color: "#fff",
    //                             padding: "10px 16px",
    //                             border: "none",
    //                             borderRadius: "4px",
    //                             cursor: "pointer",
    //                             fontSize: "14px",
    //                             width: "180px",
    //                         }}
    //                     >
    //                         <FaUserPlus style={{ marginRight: "8px" }} />
    //                         Add to Waitlist
    //                     </button>
    //                 </div>
    //             </div>
    //         )}

    //         {showSeatWaitlistGuestModal && seatingWaitlistGuestInfo && (
    //             <div style={{ ...modalStyle, maxWidth: "500px" }}>
    //                 <div style={modalHeaderStyle}>
    //                     <h3 style={{ margin: 0, fontSize: "18px" }}>
    //                         Seat Waitlisted Guest
    //                     </h3>
    //                     <button
    //                         style={modalCloseButtonStyle}
    //                         onClick={() => {
    //                             setShowSeatWaitlistGuestModal(false);
    //                             setSeatingWaitlistGuestInfo(null);
    //                         }}
    //                         title="Close"
    //                     >
    //                         <FaTimes />
    //                     </button>
    //                 </div>
    //                 <div style={detailsBoxStyle}>
    //                     <p>
    //                         <strong>Guest:</strong> {seatingWaitlistGuestInfo.entry.guestName}
    //                     </p>
    //                     <p>
    //                         <strong>Party Size:</strong>{" "}
    //                         {seatingWaitlistGuestInfo.entry.capacity}
    //                     </p>
    //                     <p>
    //                         <strong>Phone:</strong>{" "}
    //                         {seatingWaitlistGuestInfo.entry.phoneNumber || "N/A"}
    //                     </p>
    //                 </div>

    //                 <div style={inputContainerStyle}>
    //                     <label htmlFor="selectTableForWaitlist" style={labelStyle}>
    //                         Select Available Table (Capacity {">="}{" "}
    //                         {seatingWaitlistGuestInfo.entry.capacity}):
    //                     </label>
    //                     {availableTablesForWaitlistGuest.length > 0 ? (
    //                         <select
    //                             id="selectTableForWaitlist"
    //                             value={selectedTableForWaitlistSeating}
    //                             onChange={(e) =>
    //                                 setSelectedTableForWaitlistSeating(e.target.value)
    //                             }
    //                             style={inputFieldStyle}
    //                         >
    //                             <option value="">-- Select a Table --</option>
    //                             {availableTablesForWaitlistGuest.map((table) => (
    //                                 <option key={table.id} value={table.id}>
    //                                     {table.shortId} (Cap: {table.capacityValue}, Floor:{" "}
    //                                     {table.floorName})
    //                                 </option>
    //                             ))}
    //                         </select>
    //                     ) : (
    //                         <p
    //                             style={{
    //                                 color: statusColors.Occupied,
    //                                 fontSize: "13px",
    //                                 marginTop: "5px",
    //                             }}
    //                         >
    //                             No suitable tables are currently available based on party
    //                             size.
    //                         </p>
    //                     )}
    //                 </div>
    //                 <p
    //                     style={{
    //                         fontSize: "11px",
    //                         color: "#333",
    //                         marginTop: "-8px",
    //                         marginBottom: "10px",
    //                     }}
    //                 >
    //                     Seating at: {actualCurrentDate} {actualCurrentTime} for{" "}
    //                     {90} mins (Restaurant Hours:{" "}
    //                     {RESTAURANT_OPEN_TIME_STR} - {RESTAURANT_CLOSE_TIME_STR}
    //                     ).
    //                 </p>

    //                 <div style={buttonContainerStyle}>
    //                     <button
    //                         onClick={handleConfirmSeatWaitlistGuest}
    //                         style={{
    //                             ...buttonStyle,
    //                             width: "180px",
    //                             backgroundColor: statusColors.Available,
    //                         }}
    //                         disabled={
    //                             !selectedTableForWaitlistSeating ||
    //                             availableTablesForWaitlistGuest.length === 0
    //                         }
    //                     >
    //                         <FaBookmark style={{ marginRight: "8px" }} /> Seat Guest
    //                     </button>
    //                 </div>
    //             </div>
    //         )}

    //         {(showFloorEditModal ||
    //             showBookTableModal ||
    //             showIndividualTableModal ||
    //             showAddToWaitlistModal ||
    //             showSeatWaitlistGuestModal) && (
    //                 <div
    //                     style={{
    //                         position: "fixed",
    //                         top: 0,
    //                         left: 0,
    //                         right: 0,
    //                         bottom: 0,
    //                         backgroundColor: "rgba(0,0,0,0.5)",
    //                         zIndex: 999,
    //                     }}
    //                 />
    //             )}
    //     </div>
    // );

};

// --- STYLES (Copied from your provided code) ---
const pageContainerStyle = {
    // Styles as provided
};
const mainTableSectionStyle = {
    flexGrow: 7,
    flexShrink: 1,
    flexBasis: "0%",
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    overflowY: "auto",
    scrollbarWidth: "none",
    scrollbarColor: "#a0a0a0 #f0f0f0",
    // paddingTop: "15px",
};
const bookingsViewSectionStyle = {
    flexGrow: 3,
    flexShrink: 1,
    flexBasis: "0%",
    padding: "5px",
    boxSizing: "border-box",
    overflowY: "auto",
    // height: "100vh",
    borderLeft: window.innerWidth < 768 ? "0px" : "0px solid #ddd",
    backgroundColor: "#fdfdff",
    scrollbarWidth: "none",
    scrollbarColor: "#a0a0a0 #f0f0f0",
};
const globalControlsContainerStyle = {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    marginBottom: "15px",
    padding: "10px 15px",
    backgroundColor: "#f8f9fa",
    borderRadius: "5px",
    border: "1px solid #dee2e6",
    flexWrap: "wrap",
    flexShrink: 0,
};
const globalControlItemStyle = {
    display: "flex",
    alignItems: "center",
    gap: "8px",
};
const legendContainerStyle = {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: "5px 0px",
    fontSize: "11px",
    flexWrap: "wrap",
    lineHeight: "1.2",
    justifyContent: "center",
    marginBottom: "10px",
    flexShrink: 0,
};
const legendTitleStyle = {
    margin: 0,
    marginRight: "3px",
    fontWeight: "500",
    fontSize: "12px",
};
const legendItemStyle = { display: "flex", alignItems: "center", gap: "4px" };
const legendColorBoxStyle = {
    width: "12px",
    height: "12px",
    borderRadius: "3px",
    border: "1px solid rgba(0,0,0,0.2)",
};
const sharedButtonStyles = {
    border: "none",
    borderRadius: "5px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "bold",
    padding: "0 15px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "background-color 0.2s ease-in-out, box-shadow 0.1s ease",
};
const buttonStyle = {
    ...sharedButtonStyles,
    height: "36px",
    backgroundColor: "#b15cd6",
    color: "white",
    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
};
const smallButtonStyle = {
    ...sharedButtonStyles,
    height: "auto",
    fontSize: "12px",
    fontWeight: "500",
    padding: "6px 10px",
    lineHeight: 1.3,
};
const smallActionButtonStyle = {
    ...sharedButtonStyles,
    height: "auto",
    minWidth: "auto",
    fontSize: "11px",
    fontWeight: "500",
    padding: "4px 8px",
    lineHeight: 1.2,
    boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
};
const tableHeaderStyle = {
    border: "1px solid #ddd",
    textAlign: "center",
    fontWeight: 500,
    backgroundColor: "#b15cd6",
    color: "white",
    position: "sticky",
    top: 0,
    zIndex: 1,
    fontSize: 'clamp(13px, 2vw, 16px)',
    whiteSpace: "nowrap",
    verticalAlign: "middle",
};
const tableCellStyle = {
    border: "1px solid #ddd",
    textAlign: "center",
    verticalAlign: "middle",
    fontSize: 'clamp(13px, 2vw, 16px)',
    whiteSpace: "nowrap",
};
const evenRowStyle = { backgroundColor: "#fdf6ff" };
const oddRowStyle = { backgroundColor: "#fff" };
const actionButtonStyle = {
    backgroundColor: "transparent",
    color: "#555",
    border: "none",
    cursor: "pointer",
    borderRadius: "5px",
    margin: "0 2px",
    fontSize: "17px",
    padding: "5px",
    lineHeight: 1,
    display: "inline-flex",
    alignItems: "center",
    transition: "color 0.2s",
};
const modalStyle = {
    position: "fixed",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    backgroundColor: "white",
    padding: "20px 25px",
    borderRadius: "10px",
    boxShadow: "0 5px 20px rgba(0, 0, 0, 0.25)",
    zIndex: 1000,
    width: "90%",
    maxWidth: "480px",
    maxHeight: "90vh",
    overflowY: "auto",
    boxSizing: "border-box",
    scrollbarWidth: "thin",
    scrollbarColor: "#a0a0a0 #f0f0f0",
};
const modalHeaderStyle = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "15px",
    borderBottom: "1px solid #eee",
    paddingBottom: "10px",
};
const modalCloseButtonStyle = {
    background: "transparent",
    border: "none",
    fontSize: "20px",
    color: "#777",
    cursor: "pointer",
    padding: "5px",
};
const inputContainerStyle = { marginBottom: "12px" };
const labelStyle = {
    display: "block",
    marginBottom: "5px",
    fontWeight: "500",
    fontSize: "13px",
    color: "#333",
};
const inputFieldStyle = {
    width: "100%",
    padding: "9px 10px",
    fontSize: "13px",
    borderRadius: "5px",
    border: "1px solid #ccc",
    boxSizing: "border-box",
    transition: "border-color 0.2s, box-shadow 0.2s",
};
const buttonContainerStyle = {
    display: "flex",
    justifyContent: "center",
    marginTop: "20px",
    paddingTop: "15px",
    borderTop: "1px solid #eee",
};
const paginationButtonStyle = {
    ...sharedButtonStyles,
    height: "32px",
    minWidth: "70px",
    backgroundColor: "rgb(177, 92, 214)",
    color: "#fff",
    margin: "0 5px",
    padding: "0 10px",
};
const tableVisualContainerStyle = {
    display: "flex",
    flexWrap: "wrap",
    gap: "8px",
    padding: "5px 0",
    justifyContent: "flex-start",
    alignItems: "center",
};
const individualTableStyle = {
    width: "35px",
    height: "35px",
    borderRadius: "4px",
    border: "1px solid rgba(0,0,0,0.15)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "14px",
    fontWeight: "bold",
    color: "white",
    boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
    WebkitTextStroke: "0.5px rgba(0,0,0,0.5)",
    textStroke: "0.5px rgba(0,0,0,0.5)",
    flexShrink: 0,
    cursor: "pointer",
};
const tableRepresentationWrapperStyleNoHighlight = {
    display: "flex",
    alignItems: "center",
    gap: "5px",
    padding: "3px",
    borderRadius: "4px",
    minWidth: "auto",
};
const inputRowStyle = { display: "flex", gap: "15px", marginBottom: "12px" };
const inputContainerHalfStyle = { flex: 1 };
const detailsBoxStyle = {
    backgroundColor: "#f9f9f9",
    border: "1px solid #eee",
    borderRadius: "5px",
    padding: "8px 10px",
    marginBottom: "12px",
    fontSize: "13px",
    color: "#333",
    lineHeight: "1.5",
};
const configSectionStyle = {
    border: "1px solid #e0e0e0",
    borderRadius: "6px",
    marginBottom: "15px",
    backgroundColor: "#fdfdff",
    padding: "12px 15px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
};
const configHeaderStyle = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    paddingBottom: "10px",
    marginBottom: "10px",
    borderBottom: "1px solid #eaeaea",
    fontSize: "14px",
    fontWeight: "500",
};
const individualTableListStyleInModal = {
    maxHeight: "200px",
    overflowY: "auto",
    paddingRight: "8px",
    marginRight: "-8px",
    scrollbarWidth: "thin",
    scrollbarColor: "#a0a0a0 #f0f0f0",
};
const individualTableEditRowStyle = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "6px 3px",
    borderBottom: "1px dotted #f0f0f0",
    fontSize: "13px",
};

export default Tablereservation;

